import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
    Button, Box, Text, VStack, HStack, Progress, Badge, Flex, IconButton, Tooltip, Image,
    Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, DrawerFooter,
    FormControl, FormLabel, Input, Grid, GridItem, Spinner, useToast, Icon, Divider, useBreakpointValue,
    Tabs, TabList, TabPanels, Tab, TabPanel, Checkbox, Alert, AlertIcon, Collapse
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningTwoIcon, TimeIcon, EditIcon, DeleteIcon, CheckIcon, CopyIcon, ExternalLinkIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { FaUser, FaBoxOpen, FaFileInvoice, FaTruck, FaCog, FaExclamationCircle, FaShippingFast, FaPrint, FaFilePdf, FaBarcode, FaBoxes, FaShoppingCart, FaTrash, FaBolt, FaHandshake, FaSave, FaStore } from 'react-icons/fa';
import ProdutoImagem from "../ProdutoImagem";
import { api, processarExpedicaoVenda, getZPLConteudo, getZPLConteudoMassa, downloadArquivoProtegido, downloadEtiquetasLote, concluirEmbalagemDespacho, reverterEmbalagemDespacho, marcarEnvioExterno, downloadDanfePdf, downloadDanfeZpl, verificarSaldoMelhorEnvio, getConfiguracoes } from '../../services/api';
import PainelOcorrenciasImpressao from './PainelOcorrenciasImpressao';
import { motivoPorVenda } from './ocorrenciasImpressao';
import { baseUrlApi } from '../../config';
import { mensagemDeErro, listaDeErros } from '../../utils/erroApi';
import { SeletorEmissorNfe, BadgeEmissor, useConfigEmissorNfeMl, ehVendaMl, EMISSOR_PADRAO } from '../EmissorNfeMl';

const PADRAO_ERRO_NFE = 'Erro ao emitir NFe (a API não retornou detalhe).';

const detalhesDoErroNfe = (fonte, padrao = PADRAO_ERRO_NFE) => {
    const retorno = fonte && typeof fonte === 'object' ? fonte.retorno : null;
    const retornoObj = retorno && typeof retorno === 'object' ? retorno : {};
    const mensagemObj = retornoObj.mensagem && typeof retornoObj.mensagem === 'object' ? retornoObj.mensagem : {};

    return {
        errorMsg: mensagemDeErro(fonte, padrao),
        validationErrors: listaDeErros(mensagemObj.validationErrors || retornoObj.validationErrors),
        errorPayload: retornoObj.payload || mensagemObj.payload || null
    };
};

const extrairDetalhesErroNfe = (res, padrao = PADRAO_ERRO_NFE) => {
    const primeiroErro = (Array.isArray(res?.error) ? res.error[0] : null) || res?.erros?.[0] || null;
    const detalhes = detalhesDoErroNfe(primeiroErro || res, padrao);
    return { ...detalhes, errorMsg: mensagemDeErro(primeiroErro || res, mensagemDeErro(res, padrao)) };
};

const classificarRetornoNfe = (res, vendaIds) => {
    const listaErros = [
        ...(Array.isArray(res?.error) ? res.error : []),
        ...(Array.isArray(res?.erros) ? res.erros : [])
    ];

    const errosPorVenda = {};
    listaErros.forEach((item) => {
        if (item && item.id !== undefined && item.id !== null) errosPorVenda[String(item.id)] = item;
    });

    const idsComSucesso = new Set(
        (Array.isArray(res?.sucessos) ? res.sucessos : [])
            .map((item) => (item && item.id !== undefined && item.id !== null ? String(item.id) : null))
            .filter(Boolean)
    );

    const houveRespostaPorVenda = Object.keys(errosPorVenda).length > 0 || idsComSucesso.size > 0;
    const sucessoGlobal = res?.status === true || res?.success === true;
    const mensagemGlobal = mensagemDeErro(res, PADRAO_ERRO_NFE);

    const resultado = {};
    vendaIds.forEach((vendaId) => {
        const chave = String(vendaId);

        if (errosPorVenda[chave]) {
            resultado[vendaId] = detalhesDoErroNfe(errosPorVenda[chave]);
            return;
        }
        if (idsComSucesso.has(chave)) return;
        if (sucessoGlobal && !houveRespostaPorVenda) return;
        if (sucessoGlobal && houveRespostaPorVenda && listaErros.length === 0) return;

        resultado[vendaId] = {
            errorMsg: houveRespostaPorVenda
                ? `A API não retornou resultado para esta venda. Outros erros do lote: ${mensagemGlobal}`
                : mensagemGlobal,
            validationErrors: [],
            errorPayload: null
        };
    });

    return resultado;
};

// Helpers
const getOrigemVenda = (p) => {
    if (p.origem === "TAGA" || p.origem === "ECOMMERCE") return p.origem;
    if (p.envio?.id_mkt) {
        if (p.customerName && String(p.customerName).startsWith('LS')) return "Loja Integrada (Mkt)";
        return "Mercado Livre";
    }
    if (p.customerName && String(p.customerName).startsWith('LS')) return "E-commerce (Site)";
    return "Venda Manual / Outros";
};

const isLalamove = (p) => {
    const modo = p?.envio?.modo_envio || '';
    return String(modo).toUpperCase().includes('LALAMOVE');
};

const isEnvioExternoManual = (p) => !!p?.envio_externo;

// Retirada na loja não gera etiqueta: o cliente busca no balcão. Antes caía no
// "Melhor Envio" e o processador via Config. Frete numa venda que não tem frete.
const isRetiradaLoja = (p) => !!p?.retiradaLoja;

const isSemEtiquetaNossa = (p) => isLalamove(p) || isEnvioExternoManual(p) || isRetiradaLoja(p);

// Usado enquanto /configuracoes nao responde. Alertar de menos e pior do que
// alertar demais: sem os limites o operador escolheria frete caro sem aviso.
const LIMITES_ALERTA_FRETE_PADRAO = { percentual: 30, valor: 30 };

const formatarReais = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Diferenca de uma cotacao contra o frete que o cliente pagou no pedido.
 *
 * Retorna null quando nao ha o que comparar (frete gratis ou pedido sem valor
 * de frete registrado): mostrar "+100%" sobre zero nao ajuda ninguem.
 */
const calcularDivergenciaFrete = (precoCotacao, fretePago, limites) => {
    const preco = parseFloat(precoCotacao);
    const pago = parseFloat(fretePago);
    if (!Number.isFinite(preco) || !Number.isFinite(pago) || pago <= 0) return null;

    const diferenca = preco - pago;
    const percentual = (diferenca / pago) * 100;
    const acimaDoLimite = percentual > limites.percentual && diferenca > limites.valor;

    return { diferenca, percentual, acimaDoLimite, maisCaro: diferenca > 0 };
};

const getTipoEtiqueta = (p) => {
    if (isLalamove(p)) return "Lalamove";
    if (isRetiradaLoja(p)) return "Retirada";
    if (isEnvioExternoManual(p)) return "Externa";
    const origem = getOrigemVenda(p);
    const isMercadoLivre = origem.includes("Mercado Livre")
        || p.origem === "Mercado Livre" || p.origem === "Mercado Shops";
    if (isMercadoLivre) {
        if (p.envio?.modo_envio === 'Entrega a Combinar') {
            return "Melhor Envio";
        }
        return "Mercado Livre";
    }
    return "Melhor Envio";
};

/**
 * Ações de balcão de uma venda de retirada. Aparece nas abas Etiquetas e
 * Controle, no lugar das opções de frete, que não fazem sentido para quem
 * busca a peça na loja.
 *
 * Liberar é automático na conferência: aqui o botão só mostra o estado e
 * serve para desfazer. Concluir só existe depois de liberar: entregar antes
 * seria dar baixa numa peça que ninguém confirmou estar no balcão.
 */
const AcoesRetirada = ({ venda, onProntoRetirada, onRetiradaColetada }) => {
    if (!venda) return null;

    const liberado = !!venda.prontoRetiradaAt;

    return (
        <>
            <Tooltip label={
                liberado
                    ? (venda.clienteAvisadoRetirada
                        ? 'Cliente já avisado. Clique para desfazer a liberação'
                        : 'Liberado. O cliente é avisado em até 5 minutos. Clique para desfazer')
                    : 'Libera sozinho quando todos os itens forem conferidos. Clique para liberar agora'
            }>
                <Button
                    size="xs"
                    colorScheme={liberado ? 'green' : 'orange'}
                    variant={liberado ? 'solid' : 'outline'}
                    leftIcon={liberado ? <CheckCircleIcon /> : <FaStore />}
                    isDisabled={!onProntoRetirada}
                    onClick={() => onProntoRetirada && onProntoRetirada(venda)}
                >
                    {liberado ? 'Pronto p/ Retirada' : 'Liberar Retirada'}
                </Button>
            </Tooltip>

            {liberado && (
                <Tooltip label={venda.retiradaColetada
                    ? 'Venda concluída. O cliente recebeu o aviso de pedido entregue'
                    : 'O cliente retirou a peça? Isto conclui a venda'}>
                    <Button
                        size="xs"
                        colorScheme={venda.retiradaColetada ? 'gray' : 'blue'}
                        leftIcon={venda.retiradaColetada ? <CheckCircleIcon /> : <FaHandshake />}
                        isDisabled={venda.retiradaColetada || !onRetiradaColetada}
                        onClick={() => onRetiradaColetada && onRetiradaColetada(venda)}
                    >
                        {venda.retiradaColetada ? 'Coletado' : 'Pedido Coletado'}
                    </Button>
                </Tooltip>
            )}
        </>
    );
};

// Componente reutilizável para exibir um produto com detalhes
const ProductRow = ({ product, compact = false }) => (
    <Box py={1.5}>
        <HStack spacing={2.5} align="flex-start">
            <ProdutoImagem produto={product.produtoEstoque} origem={null} width={compact ? "32px" : "40px"} height={compact ? "32px" : "40px"} imagemAPI={product.imageUrl} />
            <Box flex={1} minW={0}>
                <Text fontSize="xs" fontWeight="bold" color="gray.800" noOfLines={2} lineHeight="short">
                    {product.name}
                </Text>
                <Flex wrap="wrap" gap={1} mt={0.5}>
                    {product.id && (
                        <Text fontSize="10px" color="gray.500" whiteSpace="nowrap">
                            <Text as="span" fontWeight="bold" color="gray.600">SKU:</Text> {product.id}
                        </Text>
                    )}
                    {product.globalCode && (
                        <Text fontSize="10px" color="gray.500" whiteSpace="nowrap">
                            <Text as="span" fontWeight="bold" color="gray.600">Global:</Text> {product.globalCode}
                        </Text>
                    )}
                    {product?.produtoEstoque?.codigo_fornecedor && (
                        <Text fontSize="10px" color="gray.500" whiteSpace="nowrap">
                            <Text as="span" fontWeight="bold" color="gray.600">Forn:</Text> {product.produtoEstoque.codigo_fornecedor}
                        </Text>
                    )}
                    {product.brand && (
                        <Text fontSize="10px" color="gray.500" whiteSpace="nowrap">
                            <Text as="span" fontWeight="bold" color="gray.600">Marca:</Text> {product.brand}
                        </Text>
                    )}
                </Flex>
            </Box>
            <VStack spacing={0.5} align="flex-end" flexShrink={0}>
                {product.quantity > 1 && (
                    <Badge colorScheme="blue" fontSize="xs">x{product.quantity}</Badge>
                )}
                <Badge colorScheme="teal" variant="outline" fontSize="9px">{product.fornecedor}</Badge>
            </VStack>
        </HStack>
    </Box>
);

// Cabeçalho de informações da venda
const VendaInfoHeader = ({ products, vendaId, kitPacks }) => {
    const firstProduct = products[0];
    const envio = firstProduct.envio || {};
    const isFlex = envio.flex_ml === 1;
    const modoEnvio = envio.modo_envio;
    const kitInfo = (kitPacks && firstProduct.packId) ? kitPacks[firstProduct.packId] : null;
    let origemVenda = getOrigemVenda(firstProduct);
    if (origemVenda === 'Mercado Livre' && firstProduct.conta) {
        origemVenda = `Mercado Livre (${firstProduct.conta})`;
    }

    return (
        <VStack align="stretch" spacing={1} flex={1} minW={0}>
            {kitInfo && (
                <Box bg="orange.400" color="white" px={2} py={1} borderRadius="md" boxShadow="sm">
                    <HStack spacing={1} align="center">
                        <Icon as={FaBoxes} boxSize={3} />
                        <Text fontSize="10px" fontWeight="bold" lineHeight="1.2">KIT • {kitInfo.count} pedidos vinculados — despache juntos</Text>
                    </HStack>
                    <Text fontSize="9px" fontWeight="normal" lineHeight="1.3" wordBreak="break-all">
                        Pack {firstProduct.packId} • {kitInfo.orderIds.join(', ')}
                    </Text>
                </Box>
            )}
            <Flex wrap="wrap" gap={1.5} align="center" minW={0}>
            <VStack align="flex-start" spacing={0}>
                <Badge colorScheme="teal" variant="outline" fontSize="xs" px={1.5}>
                    {firstProduct.orderId || vendaId}
                </Badge>
                {firstProduct.packId && (
                    <Text fontSize="9px" color="purple.500" fontWeight="semibold" px={1.5}>
                        Pack: {firstProduct.packId}
                    </Text>
                )}
            </VStack>
            {products.length > 1 && (
                <Badge colorScheme="purple" fontSize="xs">{products.length} itens</Badge>
            )}
            <Badge colorScheme="gray" variant="subtle" fontSize="10px">
                {origemVenda}
            </Badge>
            {modoEnvio && (
                <Badge colorScheme="blue" variant="subtle" fontSize="10px" display="flex" alignItems="center">
                    <Icon as={FaShippingFast} mr={1} boxSize={2.5} />{modoEnvio}
                </Badge>
            )}
            {isFlex && (
                <Box w="20px" h="20px" flexShrink={0}>
                    <Image src="/logo_flex_ml.webp" alt="Flex" w="100%" h="100%" objectFit="contain" borderRadius="full" />
                </Box>
            )}
            {firstProduct.customerName && (
                <HStack spacing={1}>
                    <Icon as={FaUser} color="gray.400" boxSize={2.5} />
                    <Text fontSize="10px" color="gray.600" fontWeight="medium" maxW="120px" isTruncated>{firstProduct.customerName}</Text>
                </HStack>
            )}
            </Flex>
        </VStack>
    );
};

// Agrupa produtos por vendaId
const groupByVenda = (products) => {
    const groups = {};
    products.forEach(p => {
        if (!groups[p.vendaId]) {
            groups[p.vendaId] = [];
        }
        groups[p.vendaId].push(p);
    });
    return groups;
};

export const ModalEmitirNfeDespacho = ({ isOpen, onClose, selectedProducts = [], onProcessComplete, incompleteVendaIds = new Set(), onRemoveItem, onMarkAsProcessed, onRevertPacked, onProntoRetirada, onRetiradaColetada, initialTab = 0 }) => {
    const toast = useToast();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const userString = window.localStorage.getItem("user");
    const user = JSON.parse(userString);

    const [currentTabIndex, setCurrentTabIndex] = useState(0);

    // ESTADOS - NFE (por vendaId)
    const [processStatus, setProcessStatus] = useState({});
    const [isRunning, setIsRunning] = useState(false);
    const [isFinished, setIsFinished] = useState(false);
    // Vendas de retirada em que a TAGA e quem envia (Azul Cargo etc.): a nota sai
    // CIF em vez do FOB padrao. { vendaId: true }
    const [freteCif, setFreteCif] = useState({});
    // Emissor das vendas do ML neste lote: padrao de cada conta, interno ou faturador do ML.
    const [emissorNfe, setEmissorNfe] = useState(EMISSOR_PADRAO);
    const configEmissor = useConfigEmissorNfeMl(isOpen);

    // ESTADOS - ETIQUETAS (por vendaId)
    const [etiquetaStatus, setEtiquetaStatus] = useState({});
    const [isRunningEtiquetas, setIsRunningEtiquetas] = useState(false);
    const [isFinishedEtiquetas, setIsFinishedEtiquetas] = useState(false);

    // DRAFTS - MELHOR ENVIO
    const [draftsME, setDraftsME] = useState({}); // { vendaId: { isReady: true, servico_id, custom_payload, preco, nomeEtiqueta } }
    const [editingDraftVendaId, setEditingDraftVendaId] = useState(null);
    const [draftMePayload, setDraftMePayload] = useState(null);
    const [draftTransportadoraCombinada, setDraftTransportadoraCombinada] = useState(null);
    const [draftMeCotacoes, setDraftMeCotacoes] = useState([]);
    const [showDestinatario, setShowDestinatario] = useState(false);
    const [isDraftMeLoading, setIsDraftMeLoading] = useState(false);
    const [limitesAlertaFrete, setLimitesAlertaFrete] = useState(LIMITES_ALERTA_FRETE_PADRAO);
    const [salvandoDimensoes, setSalvandoDimensoes] = useState(false);
    const [draftMeError, setDraftMeError] = useState("");

    // Drawer de Correção NFE
    const [editingVendaId, setEditingVendaId] = useState(null);
    const [editingPayload, setEditingPayload] = useState(null);

    // ESTADOS - CONTROLE / Embalagem (por uniqueId do produto)
    const [packedStatus, setPackedStatus] = useState({});
    const [localReverted, setLocalReverted] = useState(new Set());
    const [loadingActions, setLoadingActions] = useState({}); // { 'vendaId-action': true }
    const [loadingBulk, setLoadingBulk] = useState({}); // { 'actionName': true }
    const [incluirDanfeNaEtiqueta, setIncluirDanfeNaEtiqueta] = useState(true);
    const [ocorrenciasImpressao, setOcorrenciasImpressao] = useState(null);
    const motivosImpressaoPorVenda = useMemo(
        () => motivoPorVenda(ocorrenciasImpressao?.mensagens),
        [ocorrenciasImpressao]
    );
    const painelOcorrenciasRef = useRef(null);

    useEffect(() => {
        const alvo = painelOcorrenciasRef.current;
        if (!ocorrenciasImpressao || !alvo || typeof alvo.scrollIntoView !== 'function') return;
        try {
            alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {
            alvo.scrollIntoView();
        }
    }, [ocorrenciasImpressao]);

    // Agrupamento por vendaId
    const vendaGroups = useMemo(() => groupByVenda(selectedProducts), [selectedProducts]);
    const vendaIds = useMemo(() => Object.keys(vendaGroups).map(Number), [vendaGroups]);
    const temVendaMl = useMemo(() => vendaIds.some(vId => ehVendaMl(vendaGroups[vId]?.[0]?.origem)), [vendaIds, vendaGroups]);

    // Agrupamento por pack: vendas do mesmo pack_id sao emitidas em uma unica chamada
    // Chave: "pack:<packId>" se packId existe, senao "venda:<vendaId>"
    const packGroups = useMemo(() => {
        const groups = {};
        vendaIds.forEach(vId => {
            const firstProduct = vendaGroups[vId]?.[0];
            const packId = firstProduct?.packId;
            const key = packId ? `pack:${packId}` : `venda:${vId}`;
            if (!groups[key]) groups[key] = [];
            if (!groups[key].includes(vId)) groups[key].push(vId);
        });
        return groups;
    }, [vendaIds, vendaGroups]);

    const kitPacks = useMemo(() => {
        const byPack = {};
        selectedProducts.forEach(p => {
            if (!p.packId) return;
            if (!byPack[p.packId]) byPack[p.packId] = new Map();
            if (!byPack[p.packId].has(p.vendaId)) {
                byPack[p.packId].set(p.vendaId, p.orderId || p.vendaId);
            }
        });
        const result = {};
        Object.entries(byPack).forEach(([packId, m]) => {
            if (m.size > 1) result[packId] = { count: m.size, orderIds: [...m.values()] };
        });
        return result;
    }, [selectedProducts]);

    useEffect(() => {
        if (isOpen) {
            setCurrentTabIndex(initialTab || 0);
            // A escolha de emissor vale so para o lote aberto: o proximo comeca no padrao.
            setEmissorNfe(EMISSOR_PADRAO);
        }
    }, [isOpen, initialTab]);

    useEffect(() => {
        if (!isOpen) return;
        let cancelado = false;
        getConfiguracoes().then(lista => {
            if (cancelado || !Array.isArray(lista)) return;
            const ler = (nome, padrao) => {
                const item = lista.find(c => c.nome === nome);
                const valor = parseFloat(item?.valor);
                return Number.isFinite(valor) && valor > 0 ? valor : padrao;
            };
            setLimitesAlertaFrete({
                percentual: ler('frete_alerta_percentual', LIMITES_ALERTA_FRETE_PADRAO.percentual),
                valor: ler('frete_alerta_valor', LIMITES_ALERTA_FRETE_PADRAO.valor),
            });
        });
        return () => { cancelado = true; };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            setIsRunning(false);
            setIsRunningEtiquetas(false);
            setFreteCif({});
            setEditingVendaId(null);
            setEditingDraftVendaId(null);
            setDraftMePayload(null);
            setDraftMeCotacoes([]);
            setDraftMeError("");

            const initialStatus = {};
            const initialEtiquetaStatus = {};
            const initialDrafts = {};
            const initialPacked = {};

            vendaIds.forEach(vendaId => {
                const products = vendaGroups[vendaId];
                const firstProduct = products[0];

                const allNfe = products.every(p => p.nfeEmitida);
                initialStatus[vendaId] = { status: allNfe ? 'sucesso' : 'aguardando' };

                const isML = getTipoEtiqueta(firstProduct) === "Mercado Livre";
                const semEtq = isSemEtiquetaNossa(firstProduct);
                const allEtq = products.every(p => p.etiquetaEmitida) || (allNfe && isML) || semEtq;
                const isNoCarrinho = !allEtq && products.some(p => p.etiquetaNoCarrinho);

                if ((allNfe && isML) || semEtq) {
                    products.forEach(p => p.etiquetaEmitida = true);
                }

                initialEtiquetaStatus[vendaId] = {
                    status: allEtq ? 'sucesso' : (isNoCarrinho ? 'carrinho' : 'aguardando'),
                    externoId: firstProduct.etiquetaExternoId || null,
                    naoAplica: semEtq,
                };

                if (getTipoEtiqueta(firstProduct) === 'Melhor Envio') {
                    if (allEtq) {
                        initialDrafts[vendaId] = { isReady: true, cotacaoSelecionada: 'Já gerada' };
                    } else {
                        try {
                            const savedDrafts = JSON.parse(localStorage.getItem('TAGA_despacho_draftsME') || '{}');
                            initialDrafts[vendaId] = savedDrafts[vendaId] || null;
                        } catch (e) {
                            initialDrafts[vendaId] = null;
                        }
                    }
                }

                products.forEach(p => {
                    const isAlreadyPacked = p.verificationDetails?.status === 3;
                    initialPacked[p.uniqueId] = isAlreadyPacked;
                });
            });

            const allNfeDone = vendaIds.every(vId => vendaGroups[vId].every(p => p.nfeEmitida));
            const allEtqDone = vendaIds.every(vId => vendaGroups[vId].every(p => p.etiquetaEmitida));

            setIsFinished(allNfeDone);
            setIsFinishedEtiquetas(allEtqDone);

            setProcessStatus(initialStatus);
            setEtiquetaStatus(initialEtiquetaStatus);
            setDraftsME(initialDrafts);
            setPackedStatus(initialPacked);
        }
    }, [isOpen, selectedProducts]);

    // ===== CONTADORES =====
    const nfeCompleted = vendaIds.filter(vId => processStatus[vId]?.status === 'sucesso').length;
    const nfeErrors = vendaIds.filter(vId => processStatus[vId]?.status === 'erro').length;
    const nfeLoading = vendaIds.filter(vId => processStatus[vId]?.status === 'processando').length;
    const nfePending = vendaIds.filter(vId => processStatus[vId]?.status === 'aguardando').length;
    const totalVendas = vendaIds.length;
    const nfeProgress = totalVendas > 0 ? Math.round(((nfeCompleted + nfeErrors) / totalVendas) * 100) : 0;

    // Tab 2 acessível se PELO MENOS 1 venda tem NFe emitida
    const canGoToEtiquetas = nfeCompleted > 0;
    // Vendas elegíveis para tab 2 (que já tem NFe)
    const vendasComNfe = vendaIds.filter(vId => processStatus[vId]?.status === 'sucesso');

    const etqCompleted = vendaIds.filter(vId => etiquetaStatus[vId]?.status === 'sucesso').length;
    // Tab 3 acessível se PELO MENOS 1 venda tem NFe + Etiqueta
    const vendasComEtiqueta = vendaIds.filter(vId => processStatus[vId]?.status === 'sucesso' && etiquetaStatus[vId]?.status === 'sucesso');
    const canGoToControle = vendasComEtiqueta.length > 0;

    const isAllDraftsReady = vendasComNfe.every(vId => {
        const fp = vendaGroups[vId]?.[0];
        if (!fp) return true;
        if (getTipoEtiqueta(fp) !== 'Melhor Envio') return true;
        // Se já está no carrinho ou já foi gerada, não precisa de draft
        const st = etiquetaStatus[vId]?.status;
        if (st === 'carrinho' || st === 'sucesso') return true;
        return draftsME[vId]?.isReady;
    });
    const hasMelhorEnvioInEtqTab = vendasComNfe.some(vId => {
        const fp = vendaGroups[vId]?.[0];
        return fp && getTipoEtiqueta(fp) === 'Melhor Envio';
    });
    const canPrintMassLabels = (!hasMelhorEnvioInEtqTab || isAllDraftsReady);

    const totalProducts = selectedProducts.length;
    const totalPacked = Object.values(packedStatus).filter(Boolean).length;

    const handleRemoveVenda = (vendaId) => {
        if (!onRemoveItem) return;
        const products = vendaGroups[vendaId];
        if (!products) return;
        onRemoveItem(products[0]);
    };

    // ===== LÓGICA PASSO 1 - NFE =====
    const iniciarProcessamentoNfe = async () => {
        setIsRunning(true);
        setIsFinished(false);

        const packKeysParaProcessar = Object.keys(packGroups).filter(key =>
            packGroups[key].some(vId => processStatus[vId]?.status !== 'sucesso')
        );

        for (const packKey of packKeysParaProcessar) {
            const vendaIdsDoGrupo = packGroups[packKey];

            vendaIdsDoGrupo.forEach(vId => {
                setProcessStatus(prev => ({ ...prev, [vId]: { status: 'processando' } }));
            });

            try {
                const idsStr = vendaIdsDoGrupo.join(',');
                const modalidadeFrete = vendaIdsDoGrupo.some(vId => freteCif[vId]) ? '0' : null;
                const res = await processarExpedicaoVenda(idsStr, 'nfe', user.id, null, modalidadeFrete, { emissor: emissorNfe });
                const resultado = classificarRetornoNfe(res, vendaIdsDoGrupo);

                vendaIdsDoGrupo.forEach(vendaId => {
                    const detalhes = resultado[vendaId];

                    if (!detalhes) {
                        setProcessStatus(prev => ({ ...prev, [vendaId]: { status: 'sucesso' } }));
                        let allEtqEmitted = true;
                        vendaGroups[vendaId]?.forEach(p => {
                            p.nfeEmitida = true;
                            if (getTipoEtiqueta(p) === "Mercado Livre") {
                                p.etiquetaEmitida = true;
                            }
                            if (!p.etiquetaEmitida) allEtqEmitted = false;
                        });

                        if (allEtqEmitted) {
                            setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'sucesso' } }));
                        }
                        return;
                    }

                    setProcessStatus(prev => ({
                        ...prev, [vendaId]: {
                            status: 'erro',
                            errorMsg: detalhes.errorMsg,
                            validationErrors: detalhes.validationErrors,
                            errorPayload: detalhes.errorPayload
                        }
                    }));
                });
            } catch (err) {
                const errorMsg = `Erro de conexão: ${mensagemDeErro(err, 'falha ao falar com a API.')}`;
                vendaIdsDoGrupo.forEach(vendaId => {
                    setProcessStatus(prev => ({
                        ...prev, [vendaId]: { status: 'erro', errorMsg }
                    }));
                });
            }
        }
        setIsRunning(false);
        setIsFinished(true);
    };

    // Auto-avançar quando NFe concluída sem erros
    useEffect(() => {
        if (isFinished && nfeErrors === 0 && nfeCompleted === totalVendas && totalVendas > 0 && currentTabIndex === 0) {
            toast({ title: "NFes Emitidas!", description: "Siga para Etiquetas.", status: "success", duration: 3000 });
            setCurrentTabIndex(1);
        }
    }, [isFinished, nfeErrors, nfeCompleted]);

    const reprocessarErrosNfe = () => iniciarProcessamentoNfe();

    const handleOpenEdit = (vendaId) => {
        setEditingVendaId(vendaId);
        const statusInfo = processStatus[vendaId] || {};
        const errPayload = statusInfo.errorPayload || {};
        const fp = vendaGroups[vendaId]?.[0];

        let userType = errPayload.customer?.userType || 'INDIVIDUAL';
        if (userType === 'COMPANY') userType = 'LEGAL';

        setEditingPayload({
            customer: {
                name: errPayload.customer?.name || fp?.customerName || '',
                cpf: errPayload.customer?.cpf || '',
                cnpj: errPayload.customer?.cnpj || '',
                ie: errPayload.customer?.ie || '',
                email: errPayload.customer?.email || '',
                phone: errPayload.customer?.phone || '',
                userType,
            },
            shippingAddress: {
                zipCode: errPayload.shippingAddress?.zipCode || '',
                street: errPayload.shippingAddress?.street || '',
                number: errPayload.shippingAddress?.number || 'SN',
                complement: errPayload.shippingAddress?.complement || '',
                neighborhood: errPayload.shippingAddress?.neighborhood || '',
                city: errPayload.shippingAddress?.city || '',
                state: errPayload.shippingAddress?.state || '',
                country: errPayload.shippingAddress?.country || 'Brasil',
            },
            orderNumber: errPayload.orderNumber || fp?.orderId || '',
            validationErrors: statusInfo.validationErrors || [],
        });
    };
    const handleCloseEdit = () => { setEditingVendaId(null); setEditingPayload(null); };
    const handleSaveEdit = async () => {
        if (!editingVendaId || !editingPayload) return;

        const isCompany = editingPayload.customer.userType === 'LEGAL';
        const overrideData = {
            customer: {
                name: editingPayload.customer.name,
                cpf: !isCompany ? editingPayload.customer.cpf : undefined,
                cnpj: isCompany ? editingPayload.customer.cnpj : undefined,
                ie: isCompany ? (editingPayload.customer.ie || null) : undefined,
                email: editingPayload.customer.email,
                phone: editingPayload.customer.phone,
                userType: isCompany ? 'COMPANY' : 'INDIVIDUAL',
            },
            shippingAddress: {
                zipCode: editingPayload.shippingAddress.zipCode,
                street: editingPayload.shippingAddress.street,
                number: editingPayload.shippingAddress.number,
                complement: editingPayload.shippingAddress.complement,
                neighborhood: editingPayload.shippingAddress.neighborhood,
                city: editingPayload.shippingAddress.city,
                state: editingPayload.shippingAddress.state,
                country: editingPayload.shippingAddress.country,
            },
            orderNumber: editingPayload.orderNumber,
        };

        const vendaIdToProcess = editingVendaId;
        handleCloseEdit();

        setProcessStatus(prev => ({ ...prev, [vendaIdToProcess]: { status: 'processando' } }));

        try {
            const res = await processarExpedicaoVenda(vendaIdToProcess, 'nfe', user.id, overrideData, freteCif[vendaIdToProcess] ? '0' : null, { emissor: emissorNfe });
            const isSuccess = (res.sucessos && res.sucessos.length > 0) || res.status === true || res.success === true;

            if (isSuccess) {
                setProcessStatus(prev => ({ ...prev, [vendaIdToProcess]: { status: 'sucesso' } }));
                vendaGroups[vendaIdToProcess]?.forEach(p => {
                    p.nfeEmitida = true;
                    if (getTipoEtiqueta(p) === 'Mercado Livre') p.etiquetaEmitida = true;
                });
                toast({ title: 'NFe Emitida!', description: 'Correção aplicada com sucesso.', status: 'success', duration: 3000 });
            } else {
                const detalhes = extrairDetalhesErroNfe(res);

                setProcessStatus(prev => ({
                    ...prev, [vendaIdToProcess]: {
                        status: 'erro',
                        errorMsg: detalhes.errorMsg,
                        validationErrors: detalhes.validationErrors,
                        errorPayload: detalhes.errorPayload
                    }
                }));
                toast({ title: 'Erro na correção', description: detalhes.errorMsg, status: 'error', duration: 8000, isClosable: true });
            }
        } catch (err) {
            const errorMsg = `Erro de conexão: ${mensagemDeErro(err, 'falha ao falar com a API.')}`;
            setProcessStatus(prev => ({
                ...prev, [vendaIdToProcess]: { status: 'erro', errorMsg }
            }));
            toast({ title: 'Erro de conexão', description: errorMsg, status: 'error', duration: 8000, isClosable: true });
        }
    };

    // ===== LÓGICA PASSO 2 - ETIQUETAS =====
    
    // Calcula o serviço pre-selecionado no Mercado Livre / Site
    const verificarDestaque = (cotacao) => {
        if (!editingDraftFirstProduct?.envio) return false;

        const dbEmpresa = editingDraftFirstProduct.envio.empresa || editingDraftFirstProduct.envio.forma_entrega || '';
        const dbServico = editingDraftFirstProduct.envio.modo_envio || editingDraftFirstProduct.envio.servico_nome || '';
        if (!dbEmpresa && !dbServico) return false;

        const dbFull = `${dbEmpresa}${dbServico}`.toLowerCase().replace(/[^a-z0-9]/gi, '');
        const apiEmpresa = (cotacao.company?.name || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
        const apiServico = (cotacao.name || '').toLowerCase().replace(/[^a-z0-9]/gi, '');

        if (!apiEmpresa || !dbFull.includes(apiEmpresa)) return false;
        if (apiServico && dbFull.includes(apiServico)) return true;
        if (dbFull === apiEmpresa) return true;
        return false;
    };

    const renderResumoDestinatario = () => {
        const t = draftMePayload?.to;
        if (!t) return null;
        const nome = t.name || 'Sem nome';
        const doc = t.document || t.company_document || '';
        const enderecoLinha = [t.address, t.number].filter(Boolean).join(', ');
        const bairro = t.district || '';
        const cidadeUf = [t.city, t.state_abbr].filter(Boolean).join(' / ');
        const cep = t.postal_code || '';

        return (
            <Box mb={3} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                <Flex align="flex-start" gap={2.5}>
                    <Icon as={FaUser} color="gray.500" boxSize={4} mt={0.5} flexShrink={0} />
                    <Box flex="1" minW={0}>
                        <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>
                            {nome}{doc && <Text as="span" fontWeight="normal" color="gray.500" fontSize="xs"> · {doc}</Text>}
                        </Text>
                        <Text fontSize="xs" color="gray.600" lineHeight="1.4">
                            {enderecoLinha}{bairro && ` — ${bairro}`}
                        </Text>
                        <Text fontSize="xs" color="gray.600" lineHeight="1.4">
                            {cidadeUf}{cep && ` · CEP ${cep}`}
                        </Text>
                    </Box>
                </Flex>
            </Box>
        );
    };

    const renderBannerFreteOriginal = () => {
        const dbEmpresa = editingDraftFirstProduct?.envio?.empresa || editingDraftFirstProduct?.envio?.forma_entrega || '';
        const dbServico = editingDraftFirstProduct?.envio?.servico_nome || editingDraftFirstProduct?.envio?.modo_envio || '';
        const temFreteOriginal = !!(dbEmpresa || dbServico);

        if (temFreteOriginal) {
            return (
                <Box
                    mb={3}
                    px={3}
                    py={2}
                    bgGradient="linear(to-r, blue.500, blue.600)"
                    borderRadius="md"
                    boxShadow="md"
                    color="white"
                >
                    <Flex align="center" gap={2.5} wrap="wrap">
                        <Icon as={FaTruck} color="white" boxSize={4} flexShrink={0} />
                        <Text fontSize="xs" color="blue.100" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" flexShrink={0}>
                            Frete do cliente:
                        </Text>
                        <Text fontSize="sm" fontWeight="extrabold" color="white" noOfLines={1}>
                            {dbEmpresa || 'Empresa não informada'}
                            {dbServico && <Text as="span" color="blue.100"> — {dbServico}</Text>}
                        </Text>
                    </Flex>
                    <Flex align="center" gap={2} mt={1.5} pl={6} wrap="wrap">
                        <Text fontSize="xs" color="blue.100" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" flexShrink={0}>
                            Pago pelo cliente:
                        </Text>
                        {fretePagoCliente > 0 ? (
                            <Text fontSize="sm" fontWeight="extrabold" color="white">
                                {formatarReais(fretePagoCliente)}
                                <Text as="span" fontSize="xs" color="blue.100" fontWeight="normal"> · referência das comparações abaixo</Text>
                            </Text>
                        ) : (
                            <Text fontSize="sm" color="white" fontStyle="italic">
                                {fretePagoCliente === 0 ? 'Frete grátis, sem valor de referência' : 'Não registrado no pedido'}
                            </Text>
                        )}
                    </Flex>
                </Box>
            );
        }
        if (draftTransportadoraCombinada) return null;
        return (
            <Box
                mb={3}
                px={3}
                py={2}
                bgGradient="linear(to-r, orange.400, orange.500)"
                borderRadius="md"
                boxShadow="md"
                color="white"
            >
                <Flex align="center" gap={2.5}>
                    <Icon as={FaExclamationCircle} color="white" boxSize={4} flexShrink={0} />
                    <Text fontSize="xs" color="orange.100" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" flexShrink={0}>
                        Frete do cliente:
                    </Text>
                    <Text fontSize="sm" color="white" fontStyle="italic" noOfLines={1}>
                        Não informado pelo marketplace
                    </Text>
                </Flex>
            </Box>
        );
    };

    const renderBannerTransportadoraCombinada = () => {
        if (!draftTransportadoraCombinada) return null;
        return (
            <Box
                mb={3}
                px={3}
                py={2.5}
                bgGradient="linear(to-r, purple.500, purple.600)"
                borderRadius="md"
                boxShadow="md"
                color="white"
            >
                <Flex
                    direction={{ base: "column", sm: "row" }}
                    align={{ base: "flex-start", sm: "center" }}
                    gap={{ base: 1, sm: 2.5 }}
                >
                    <HStack spacing={2} flexShrink={0}>
                        <Icon as={FaHandshake} color="white" boxSize={4} flexShrink={0} />
                        <Text fontSize="xs" color="purple.100" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" whiteSpace="nowrap">
                            Combinado com o cliente:
                        </Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="extrabold" color="white" lineHeight="1.3" wordBreak="break-word">
                        {draftTransportadoraCombinada}
                    </Text>
                </Flex>
            </Box>
        );
    };

    const handleOpenDraftConfig = async (vendaId) => {
        setEditingDraftVendaId(vendaId);
        setDraftMePayload(null);
        setDraftMeCotacoes([]);
        setDraftTransportadoraCombinada(null);
        setIsDraftMeLoading(true);
        setDraftMeError("");
        try {
            const response = await api.post('/melhor-envio/preparar-payload', { venda_id: vendaId, dimensoes_vazias: true });
            if (response.data.success) {
                const dataPayload = response.data.payload;
                if (dataPayload?.volumes) {
                    dataPayload.volumes = dataPayload.volumes.map(v => ({
                        ...v,
                        _valor: formatarValorBR(v._valor)
                    }));
                }
                if (dataPayload?.to && !dataPayload.to.phone) {
                    dataPayload.to.phone = '11999999999';
                }
                setDraftMePayload(dataPayload);
                setDraftTransportadoraCombinada(response.data.transportadora_combinada || null);
            }
        } catch (error) {
            setDraftMeError(error.response?.data?.error || 'Erro ao carregar dados do envio.');
        } finally {
            setIsDraftMeLoading(false);
        }
    };

    const handleCloseDraftConfig = () => {
        setEditingDraftVendaId(null);
        setShowDestinatario(false);
        setTimeout(() => {
            setDraftMePayload(null);
            setDraftTransportadoraCombinada(null);
            setDraftMeCotacoes([]);
            setDraftMeError("");
        }, 300);
    };

    const formatarValorBR = (val) => {
        if (val === '' || val === null || val === undefined) return '';
        const s = String(val);
        if (s.includes(',')) return s;
        const n = Number(s);
        return isNaN(n) ? '' : n.toFixed(2).replace('.', ',');
    };

    const formatarEstimativa = (val) => {
        if (val === '' || val === null || val === undefined) return '-';
        const n = Number(String(val).replace(',', '.'));
        if (isNaN(n)) return '-';
        return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
    };

    const handleSalvarDimensoes = async () => {
        setSalvandoDimensoes(true);
        try {
            await api.post('/melhor-envio/salvar-dimensoes', { venda_id: editingDraftVendaId, volumes: draftMePayload.volumes });
            toast({ title: 'Dimensoes salvas', status: 'success', duration: 3000, isClosable: true });
        } catch (error) {
            toast({ title: 'Erro ao salvar dimensoes', description: error.response?.data?.error || 'Erro inesperado.', status: 'error', isClosable: true });
        } finally {
            setSalvandoDimensoes(false);
        }
    };

    const recalcularSeguro = (volumes) => {
        return volumes.reduce((acc, v) => {
            const val = parseFloat(String(v._valor || '0').replace(',', '.'));
            return acc + (isNaN(val) ? 0 : val);
        }, 0);
    };

    const handleVolumeChange = (index, field, value) => {
        let formattedValue = String(value);
        if (field !== '_nome') {
            formattedValue = formattedValue.replace('.', ',');
            formattedValue = formattedValue.replace(/[^0-9,]/g, '');
            const parts = formattedValue.split(',');
            if (parts.length > 2) {
                formattedValue = parts[0] + ',' + parts.slice(1).join('');
            }
            if (field === '_valor' && parts.length === 2 && parts[1].length > 2) {
                formattedValue = parts[0] + ',' + parts[1].substring(0, 2);
            }
        }
        setDraftMePayload(prev => {
            const newVolumes = [...prev.volumes];
            newVolumes[index] = { ...newVolumes[index], [field]: formattedValue };
            return {
                ...prev,
                volumes: newVolumes,
                options: { ...prev.options, insurance_value: recalcularSeguro(newVolumes) }
            };
        });
    };

    const handleAddVolume = () => {
        setDraftMePayload(prev => {
            const newVolumes = [...prev.volumes, { _nome: `Caixa ${prev.volumes.length + 1}`, height: '', width: '', length: '', weight: '', _qtd: 1, _valor: '' }];
            return { ...prev, volumes: newVolumes, options: { ...prev.options, insurance_value: recalcularSeguro(newVolumes) } };
        });
    };

    const handleRemoveVolume = (index) => {
        if (draftMePayload.volumes.length <= 1) return;
        setDraftMePayload(prev => {
            const newVolumes = [...prev.volumes];
            newVolumes.splice(index, 1);
            return { ...prev, volumes: newVolumes, options: { ...prev.options, insurance_value: recalcularSeguro(newVolumes) } };
        });
    };

    const handleToChange = (field, value) => {
        if (field === 'document') {
            const numbers = value.replace(/\D/g, '');
            if (numbers.length <= 11) {
                setDraftMePayload(prev => ({ ...prev, to: { ...(prev.to || {}), document: numbers, company_document: '' } }));
            } else {
                setDraftMePayload(prev => ({ ...prev, to: { ...(prev.to || {}), document: '', company_document: numbers } }));
            }
            return;
        }
        setDraftMePayload(prev => ({ ...prev, to: { ...(prev.to || {}), [field]: value } }));
    };

    const syncPayloadForApi = () => {
        const produtosSincronizados = draftMePayload.volumes.map((vol, idx) => {
            const val = parseFloat(String(vol._valor || '0').replace(',', '.'));
            return { name: vol._nome || `Caixa ${idx + 1}`, quantity: vol._qtd || 1, unitary_value: isNaN(val) || val <= 0 ? 100 : Number(val.toFixed(2)) };
        });
        const volumesFormatados = draftMePayload.volumes.map(vol => {
            const val = parseFloat(String(vol._valor || '0').replace(',', '.'));
            return {
                ...vol, height: parseFloat(String(vol.height || '0').replace(',', '.')), width: parseFloat(String(vol.width || '0').replace(',', '.')),
                length: parseFloat(String(vol.length || '0').replace(',', '.')), weight: parseFloat(String(vol.weight || '0').replace(',', '.')),
                _valor: isNaN(val) || val <= 0 ? 100 : Number(val.toFixed(2))
            };
        });
        return {
            ...draftMePayload, volumes: volumesFormatados, products: produtosSincronizados,
            options: { ...draftMePayload.options, insurance_value: Number(recalcularSeguro(draftMePayload.volumes).toFixed(2)) }
        };
    };

    const handleCotarFrete = async () => {
        if (!draftMePayload?.volumes || draftMePayload.volumes.length === 0) {
            toast({ title: 'Aviso', description: 'É necessário ter pelo menos 1 pacote.', status: 'warning', isClosable: true });
            return;
        }
        for (let i = 0; i < draftMePayload.volumes.length; i++) {
            const v = draftMePayload.volumes[i];
            const w = parseFloat(String(v.weight || '0').replace(',', '.'));
            const h = parseFloat(String(v.height || '0').replace(',', '.'));
            const wi = parseFloat(String(v.width || '0').replace(',', '.'));
            const l = parseFloat(String(v.length || '0').replace(',', '.'));
            if (!v._nome || v._nome.trim() === '') {
                toast({ title: 'Aviso', description: `O nome do pacote ${i + 1} não pode estar vazio.`, status: 'warning', isClosable: true });
                return;
            }
            if (isNaN(w) || isNaN(h) || isNaN(wi) || isNaN(l) || w <= 0 || h <= 0 || wi <= 0 || l <= 0) {
                toast({ title: 'Aviso', description: `As dimensões e o peso do pacote ${i + 1} devem ser formados e maiores que zero.`, status: 'warning', isClosable: true });
                return;
            }
        }
        setIsDraftMeLoading(true);
        setDraftMeError("");
        try {
            const payloadFormatado = syncPayloadForApi();
            const response = await api.post('/melhor-envio/cotar-venda', {
                venda_id: editingDraftVendaId,
                custom_payload: payloadFormatado
            });
            if (response.data.success) {
                setDraftMeCotacoes(response.data.data);
            }
        } catch (error) {
            setDraftMeError(error.response?.data?.error || 'Erro ao cotar o frete. Verifique o CEP.');
        } finally {
            setIsDraftMeLoading(false);
        }
    };

    // ===== AÇÕES DIRETAS DO CARRINHO ME =====

    // Confere o saldo do Melhor Envio ANTES de adicionar ao carrinho / comprar etiquetas.
    // `valor` = soma das cotações ainda não adicionadas; `vendaIdsNoCarrinho` = vendas cujo
    // valor pendente o backend soma sozinho. Falha de consulta não bloqueia a operação.
    const conferirSaldoMelhorEnvio = async (valor = 0, vendaIdsNoCarrinho = []) => {
        const res = await verificarSaldoMelhorEnvio(valor, vendaIdsNoCarrinho);
        if (!res || res.suficiente !== false) return { ok: true };

        const mensagem = res.mensagem || 'Saldo insuficiente na conta do Melhor Envio.';
        toast({
            title: 'Saldo insuficiente no Melhor Envio',
            description: mensagem,
            status: 'error', duration: 9000, isClosable: true,
        });
        return { ok: false, mensagem };
    };

    // Fluxo alternativo do drawer: adiciona a cotação escolhida ao CARRINHO ME
    // (cria envio + deixa pendente, sem checkout/generate). Atualiza o card para estado 'carrinho'.
    const handleAdicionarAoCarrinhoDoDrawer = async (cotacao) => {
        if (editingDraftVendaId === null) return;

        // Sem saldo, a etiqueta ficaria pendente no carrinho sem poder ser paga — avisa antes.
        const checagemSaldo = await conferirSaldoMelhorEnvio(parseFloat(cotacao.price) || 0, [editingDraftVendaId]);
        if (!checagemSaldo.ok) return;

        const payloadFormatado = syncPayloadForApi();
        setEtiquetaStatus(prev => ({ ...prev, [editingDraftVendaId]: { status: 'processando' } }));
        try {
            const res = await processarExpedicaoVenda(
                editingDraftVendaId,
                'etiqueta_carrinho',
                user.id,
                { servico_id: cotacao.id, custom_payload: payloadFormatado }
            );
            if (res && !res.error) {
                const externoId = res?.data?.id || res?.id || null;
                setEtiquetaStatus(prev => ({ ...prev, [editingDraftVendaId]: { status: 'carrinho', externoId } }));
                vendaGroups[editingDraftVendaId]?.forEach(p => {
                    p.etiquetaNoCarrinho = true;
                    if (externoId) p.etiquetaExternoId = externoId;
                });
                toast({
                    title: 'Adicionado ao Carrinho!',
                    description: 'A etiqueta está pendente no carrinho do Melhor Envio.',
                    status: 'success', duration: 3000, isClosable: true,
                });
                handleCloseDraftConfig();
            } else {
                const errorMsg = res?.error || 'Erro ao adicionar ao carrinho.';
                setEtiquetaStatus(prev => ({ ...prev, [editingDraftVendaId]: { status: 'erro', errorMsg } }));
                toast({ title: 'Erro', description: errorMsg, status: 'error', duration: 5000, isClosable: true });
            }
        } catch (err) {
            setEtiquetaStatus(prev => ({ ...prev, [editingDraftVendaId]: { status: 'erro', errorMsg: 'Erro de conexão.' } }));
            toast({ title: 'Erro de conexão', status: 'error' });
        }
    };

    // Paga um item que já está no carrinho ME (checkout + generate).
    // Chamado pelo botão "Pagar Agora" no card quando status === 'carrinho'.
    const handlePagarDoCarrinho = async (vendaId) => {
        const checagemSaldo = await conferirSaldoMelhorEnvio(0, [vendaId]);
        if (!checagemSaldo.ok) {
            setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { ...(prev[vendaId] || {}), status: 'carrinho', errorMsg: checagemSaldo.mensagem } }));
            return;
        }

        setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { ...(prev[vendaId] || {}), status: 'processando' } }));
        try {
            // Reutiliza o tipo_acao='etiqueta' que faz o fluxo completo (processarCompleto).
            // O backend detecta que já existe envio e pula o createShipment.
            const res = await processarExpedicaoVenda(vendaId, 'etiqueta', user.id);
            if (res && !res.error) {
                setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'sucesso' } }));
                vendaGroups[vendaId]?.forEach(p => {
                    p.etiquetaEmitida = true;
                    p.etiquetaNoCarrinho = false;
                });
                toast({ title: 'Etiqueta paga e gerada!', status: 'success', duration: 3000 });
            } else {
                const errorMsg = res?.error || 'Erro ao processar pagamento.';
                setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'erro', errorMsg } }));
                toast({ title: 'Erro', description: errorMsg, status: 'error', duration: 5000 });
            }
        } catch (err) {
            setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'erro', errorMsg: 'Erro de conexão.' } }));
            toast({ title: 'Erro de conexão', status: 'error' });
        }
    };

    const handleMarcarEnvioExterno = async (vendaId) => {
        const confirma = window.confirm(
            'Marcar esta venda como ENVIO EXTERNO?\n\n' +
            'Use esta opção quando a etiqueta foi gerada por fora do sistema ' +
            '(retirada, FreteNet, aéreo, Lalamove etc.). O fluxo vai avançar sem gerar etiqueta aqui.'
        );
        if (!confirma) return;

        const motivo = window.prompt('Motivo (opcional):', '') || '';

        setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { ...(prev[vendaId] || {}), status: 'processando' } }));
        try {
            const res = await marcarEnvioExterno(vendaId, user.id, motivo);
            if (res && res.success) {
                setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'sucesso', naoAplica: true } }));
                vendaGroups[vendaId]?.forEach(p => {
                    p.etiquetaEmitida = true;
                    p.envio_externo = true;
                    p.etiquetaNoCarrinho = false;
                });
                toast({
                    title: 'Marcado como Envio Externo',
                    description: 'A venda pode prosseguir sem gerar etiqueta no sistema.',
                    status: 'success', duration: 3000, isClosable: true,
                });
            } else {
                const errorMsg = res?.error || 'Erro ao marcar envio externo.';
                setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'erro', errorMsg } }));
                toast({ title: 'Erro', description: errorMsg, status: 'error', duration: 5000 });
            }
        } catch (err) {
            setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'erro', errorMsg: 'Erro de conexão.' } }));
            toast({ title: 'Erro de conexão', status: 'error' });
        }
    };

    // Remove a etiqueta do carrinho ME (libera a cotação). Volta o card para estado 'aguardando'.
    const handleRemoverDoCarrinho = async (vendaId) => {
        setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { ...(prev[vendaId] || {}), status: 'processando' } }));
        try {
            const res = await processarExpedicaoVenda(vendaId, 'remover_carrinho', user.id);
            if (res && !res.error) {
                setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'aguardando' } }));
                vendaGroups[vendaId]?.forEach(p => { p.etiquetaNoCarrinho = false; p.etiquetaExternoId = null; });
                toast({
                    title: 'Removido do Carrinho',
                    description: 'A etiqueta foi removida do carrinho Melhor Envio.',
                    status: 'success', duration: 3000,
                });
            } else {
                const errorMsg = res?.error || 'Erro ao remover do carrinho.';
                setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'carrinho', errorMsg } }));
                toast({ title: 'Erro', description: errorMsg, status: 'error', duration: 5000 });
            }
        } catch (err) {
            setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'carrinho', errorMsg: 'Erro de conexão.' } }));
            toast({ title: 'Erro de conexão', status: 'error' });
        }
    };

    const confirmarMelhorEnvioDraft = (cotacao) => {
        if (editingDraftVendaId === null) return;
        const payloadFormatado = syncPayloadForApi();
        setDraftsME(prev => {
            const novoObj = { 
                isReady: true, 
                servico_id: cotacao.id, 
                custom_payload: payloadFormatado, 
                preco: cotacao.price, 
                nomeEtiqueta: cotacao.name,
                company: cotacao.company?.name,
                companyPicture: cotacao.company?.picture,
                deliveryTime: cotacao.delivery_time
            };
            
            try {
                const globalDrafts = JSON.parse(localStorage.getItem('TAGA_despacho_draftsME') || '{}');
                globalDrafts[editingDraftVendaId] = novoObj;
                localStorage.setItem('TAGA_despacho_draftsME', JSON.stringify(globalDrafts));
            } catch (e) { console.error('Erro ao salvar rascunho', e) }

            return {
                ...prev,
                [editingDraftVendaId]: novoObj
            };
        });
        toast({ title: "Frete Salvo no Rascunho!", description: "Será gerado junto com os demais em 'Gerar Etiquetas'.", status: "success", duration: 3000, isClosable: true });
        handleCloseDraftConfig();
    };

    const iniciarProcessamentoEtiquetas = async () => {
        const vendasComNfeSet = new Set(vendasComNfe);
        const packKeysParaProcessar = Object.keys(packGroups).filter(key => {
            const vendasDoPack = packGroups[key].filter(vId => vendasComNfeSet.has(vId));
            return vendasDoPack.length > 0 && vendasDoPack.some(vId => etiquetaStatus[vId]?.status !== 'sucesso');
        });

        // Confere o saldo do Melhor Envio antes de disparar qualquer requisição:
        // vendas já no carrinho têm o valor somado no backend; as demais usam o preço do rascunho.
        const vendasNoCarrinho = [];
        let valorRascunhos = 0;
        packKeysParaProcessar.forEach(packKey => {
            const vendasDoPack = packGroups[packKey].filter(vId => vendasComNfeSet.has(vId));
            const firstVendaId = vendasDoPack[0];
            if (!firstVendaId) return;
            if (getTipoEtiqueta(vendaGroups[firstVendaId]?.[0]) !== 'Melhor Envio') return;

            if (vendasDoPack.some(vId => etiquetaStatus[vId]?.status === 'carrinho')) {
                vendasNoCarrinho.push(firstVendaId);
            } else if (draftsME[firstVendaId]?.isReady) {
                valorRascunhos += parseFloat(draftsME[firstVendaId].preco) || 0;
            }
        });

        if (vendasNoCarrinho.length > 0 || valorRascunhos > 0) {
            const checagemSaldo = await conferirSaldoMelhorEnvio(valorRascunhos, vendasNoCarrinho);
            if (!checagemSaldo.ok) return;
        }

        setIsRunningEtiquetas(true);
        setIsFinishedEtiquetas(false);

        for (const packKey of packKeysParaProcessar) {
            const vendaIdsDoGrupo = packGroups[packKey].filter(vId => vendasComNfeSet.has(vId));

            vendaIdsDoGrupo.forEach(vId => {
                setEtiquetaStatus(prev => ({ ...prev, [vId]: { status: 'processando' } }));
            });

            try {
                let overrideDataForEtiqueta = null;
                const firstVendaId = vendaIdsDoGrupo[0];
                if (draftsME[firstVendaId] && draftsME[firstVendaId].isReady) {
                    overrideDataForEtiqueta = {
                        servico_id: draftsME[firstVendaId].servico_id,
                        custom_payload: draftsME[firstVendaId].custom_payload
                    };
                }

                const idsStr = vendaIdsDoGrupo.join(',');
                const res = await processarExpedicaoVenda(idsStr, 'etiqueta', user.id, overrideDataForEtiqueta);

                if (res && !res.error) {
                    vendaIdsDoGrupo.forEach(vendaId => {
                        setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'sucesso' } }));
                        vendaGroups[vendaId]?.forEach(p => { p.etiquetaEmitida = true; });
                    });
                } else {
                    const errorMsg = res?.error || 'Erro ao gerar etiqueta.';
                    vendaIdsDoGrupo.forEach(vendaId => {
                        setEtiquetaStatus(prev => ({
                            ...prev,
                            [vendaId]: { status: 'erro', errorMsg }
                        }));
                    });
                }
            } catch (err) {
                vendaIdsDoGrupo.forEach(vendaId => {
                    setEtiquetaStatus(prev => ({ ...prev, [vendaId]: { status: 'erro', errorMsg: 'Erro de conexão.' } }));
                });
            }
        }

        setIsRunningEtiquetas(false);
        setIsFinishedEtiquetas(true);
        toast({ title: "Processamento concluído!", status: "info", duration: 3000 });
    };

    // Auto-avançar quando etiquetas concluídas
    useEffect(() => {
        if (isFinishedEtiquetas && vendasComNfe.length > 0 && vendasComNfe.every(vId => etiquetaStatus[vId]?.status === 'sucesso') && currentTabIndex === 1) {
            setCurrentTabIndex(2);
        }
    }, [isFinishedEtiquetas, etqCompleted]);

    // ===== LÓGICA PASSO 3 - CONTROLE =====
    const handleTogglePacked = (product) => {
        setPackedStatus(prev => ({ ...prev, [product.uniqueId]: !prev[product.uniqueId] }));
    };

    const handleSelectAllPacked = (e) => {
        const isChecked = e.target.checked;
        const newPacked = { ...packedStatus };
        vendasComEtiqueta.forEach(vendaId => {
            const products = vendaGroups[vendaId];
            if (products) {
                products.forEach(p => {
                    newPacked[p.uniqueId] = isChecked;
                });
            }
        });
        setPackedStatus(newPacked);
    };

    const handleConcluirControle = async () => {
        const packedVendaIds = Array.from(new Set(selectedProducts.filter(p => packedStatus[p.uniqueId]).map(p => p.vendaId)));
        
        if (packedVendaIds.length === 0) return;

        try {
            const res = await concluirEmbalagemDespacho(packedVendaIds);
            if (res) {
                toast({ title: "Expedição Concluída!", description: `${packedVendaIds.length} venda(s) movidas para Embalados.`, status: "success" });
                const packedProducts = selectedProducts.filter(p => packedStatus[p.uniqueId]);
                if (onMarkAsProcessed) onMarkAsProcessed(packedProducts);
                onProcessComplete && onProcessComplete(selectedProducts);
                onClose();
            } else {
                toast({ title: "Erro", description: "Falha ao concluir no servidor.", status: "error" });
            }
        } catch (err) {
            toast({ title: "Erro", description: "Erro de conexão.", status: "error" });
        }
    };

    // ===== ZPL & PDF & NF-e =====
    const setActionLoading = (vendaId, action, isLoading) => {
        setLoadingActions(prev => ({ ...prev, [`${vendaId}-${action}`]: isLoading }));
    };

    const registrarOcorrencias = (result, rotulo, aoTentarDeNovo = null) => {
        const detalhes = Array.isArray(result?.detalhes) ? result.detalhes : [];
        const avisos = Array.isArray(result?.avisos) ? result.avisos : [];
        const mensagens = result?.error ? detalhes : avisos;

        if (mensagens.length === 0) {
            if (result?.error) {
                setOcorrenciasImpressao(null);
                toast({ title: `Erro ao imprimir ${rotulo}`, description: result.error, status: 'error', duration: 6000, isClosable: true });
                return false;
            }
            setOcorrenciasImpressao(null);
            return true;
        }

        setOcorrenciasImpressao({
            mensagens,
            parcial: !result?.error,
            titulo: result?.error
                ? `Nenhuma etiqueta ${rotulo} saiu`
                : `Etiquetas ${rotulo}: saiu parcial`,
            aoTentarDeNovo,
        });

        return !result?.error;
    };

    const handlePrintZpl = async (vendaId) => {
        setActionLoading(vendaId, 'zpl', true);
        try {
            const danfeQs = incluirDanfeNaEtiqueta ? '&incluir_danfe=1' : '';
            const url = `/imprimir_etiqueta?id=${vendaId}&tipo=zpl2&user=${user.id}${danfeQs}`;
            const result = await downloadArquivoProtegido(url, `etiqueta_${vendaId}.zpl`);
            registrarOcorrencias(result, 'ZPL', () => handlePrintZpl(vendaId));
        } finally {
            setActionLoading(vendaId, 'zpl', false);
        }
    };

    const handlePrintPdf = async (vendaId) => {
        setActionLoading(vendaId, 'pdf', true);
        try {
            const danfeQs = incluirDanfeNaEtiqueta ? '&incluir_danfe=1' : '';
            const url = `/imprimir_etiqueta?id=${vendaId}&tipo=pdf&user=${user.id}${danfeQs}`;
            const result = await downloadArquivoProtegido(url, `etiqueta_${vendaId}.pdf`);
            registrarOcorrencias(result, 'PDF', () => handlePrintPdf(vendaId));
        } finally {
            setActionLoading(vendaId, 'pdf', false);
        }
    };

    const handlePrintNfe = async (vendaId, tipo = 'pdf') => {
        setActionLoading(vendaId, `nfe-${tipo}`, true);
        try {
            const url = `/baixar_xml_ml?id=${vendaId}&tipo=${tipo}`;
            const result = await downloadArquivoProtegido(url, `nfe_${vendaId}.${tipo}`);
            registrarOcorrencias(result, `NF-e (${tipo.toUpperCase()})`, () => handlePrintNfe(vendaId, tipo));
        } finally {
            setActionLoading(vendaId, `nfe-${tipo}`, false);
        }
    };

    /**
     * Imprime PDF das etiquetas em lote via POST.
     * Seleciona vendas marcadas (packedStatus) ou, se nenhuma,
     * todas as vendas com etiqueta gerada.
     */
    const handleBulkPrintEtiquetasPdf = async () => {
        const selectedIds = Array.from(new Set(
            selectedProducts
                .filter(p => packedStatus[p.uniqueId])
                .map(p => p.vendaId)
        ));
        const ids = selectedIds.length > 0 ? selectedIds : vendasComEtiqueta;
        if (ids.length === 0) return toast({
            title: 'Atenção', description: 'Nenhuma venda disponível para imprimir.', status: 'warning'
        });
        setLoadingBulk(prev => ({ ...prev, pdf: true }));
        try {
            const result = await downloadEtiquetasLote(ids, 'pdf', `etiquetas_pdf_lote_${ids.length}.pdf`, { incluirDanfe: incluirDanfeNaEtiqueta });
            registrarOcorrencias(result, 'PDF', () => handleBulkPrintEtiquetasPdf());
        } catch (err) {
            toast({ title: 'Erro inesperado', description: 'Falha ao baixar etiquetas PDF.', status: 'error' });
        } finally {
            setLoadingBulk(prev => ({ ...prev, pdf: false }));
        }
    };

    /**
     * Imprime ZPL das etiquetas em lote via POST.
     * Seleciona vendas marcadas (packedStatus) ou, se nenhuma,
     * todas as vendas com etiqueta gerada.
     */
    const handleBulkPrintZpl = async () => {
        const selectedIds = Array.from(new Set(
            selectedProducts
                .filter(p => packedStatus[p.uniqueId])
                .map(p => p.vendaId)
        ));
        const ids = selectedIds.length > 0 ? selectedIds : vendasComEtiqueta;
        if (ids.length === 0) return toast({
            title: 'Atenção', description: 'Nenhuma venda disponível para imprimir.', status: 'warning'
        });
        setLoadingBulk(prev => ({ ...prev, zpl: true }));
        try {
            const result = await downloadEtiquetasLote(ids, 'zpl2', `etiquetas_zpl_lote_${ids.length}.zip`, { incluirDanfe: incluirDanfeNaEtiqueta });
            registrarOcorrencias(result, 'ZPL', () => handleBulkPrintZpl());
        } catch (err) {
            toast({ title: 'Erro inesperado', description: 'Falha ao baixar etiquetas ZPL.', status: 'error' });
        } finally {
            setLoadingBulk(prev => ({ ...prev, zpl: false }));
        }
    };

    const handleBulkPrintNfes = async () => {
        if (vendasComEtiqueta.length === 0) return;
        setLoadingBulk(prev => ({ ...prev, nfe: true }));
        toast({ title: "Preparando NFes...", description: "As notas serão baixadas/abertas.", status: "info", duration: 3000 });
        
        try {
            // Abrir cada nota com um pequeno delay para não travar o navegador
            for (let i = 0; i < vendasComEtiqueta.length; i++) {
                const vId = vendasComEtiqueta[i];
                await new Promise(resolve => setTimeout(resolve, i === 0 ? 0 : 800));
                handlePrintNfe(vId, 'pdf');
            }
        } finally {
            setLoadingBulk(prev => ({ ...prev, nfe: false }));
        }
    };

    const handleCopyZpl = async (vendaId) => {
        setActionLoading(vendaId, 'copy', true);
        const loadingId = `copy-zpl-${vendaId}`;
        toast({ title: "Obtendo ZPL...", status: "info", duration: null, id: loadingId });
        try {
            const res = await getZPLConteudo(vendaId, { incluirDanfe: incluirDanfeNaEtiqueta });
            toast.close(loadingId);
            
            if (res && res.zpl) {
                await navigator.clipboard.writeText(res.zpl);
                if (registrarOcorrencias(res, 'ZPL', () => handleCopyZpl(vendaId))) {
                    toast({ title: "ZPL copiado!", status: "success", duration: 2000 });
                }
            } else {
                registrarOcorrencias(
                    { error: res?.error || 'ZPL vazio ou não disponível.', detalhes: res?.detalhes },
                    'ZPL',
                    () => handleCopyZpl(vendaId)
                );
            }
        } catch (err) {
            toast.close(loadingId);
            toast({ title: "Erro ao copiar", status: "error", duration: 2000 });
        } finally {
            setActionLoading(vendaId, 'copy', false);
        }
    };

    const handleBulkCopyZpl = async () => {
        const packedVendaIds = Array.from(new Set(selectedProducts.filter(p => packedStatus[p.uniqueId]).map(p => p.vendaId)));
        if (packedVendaIds.length === 0) return toast({ title: "Atenção", description: "Selecione produtos para copiar ZPL.", status: "warning" });
        setLoadingBulk(prev => ({ ...prev, copy: true }));
        const loadingId = 'copy-zpl-bulk';
        toast({ title: "Obtendo todos os ZPLs...", status: "info", duration: null, id: loadingId });
        
        try {
            const res = await getZPLConteudoMassa(packedVendaIds, { incluirDanfe: incluirDanfeNaEtiqueta });
            toast.close(loadingId);
            
            if (res && res.zpl) {
                await navigator.clipboard.writeText(res.zpl);
                const limpo = registrarOcorrencias(res, 'ZPL', () => handleBulkCopyZpl());
                if (limpo) {
                    toast({ title: "ZPLs copiados!", description: `${packedVendaIds.length} etiqueta(s) na área de transferência.`, status: "success", duration: 3000 });
                }
            } else {
                registrarOcorrencias(
                    { error: res?.error || 'ZPL não disponível.', detalhes: res?.detalhes },
                    'ZPL',
                    () => handleBulkCopyZpl()
                );
            }
        } catch (err) {
            toast.close(loadingId);
            toast({ title: "Erro ao copiar em massa", status: "error", duration: 2000 });
        } finally {
            setLoadingBulk(prev => ({ ...prev, copy: false }));
        }
    };

    const handleIndividualDanfe = async (vendaId, formato) => {
        const loadingKey = `${vendaId}-danfe-${formato}`;
        setLoadingActions(prev => ({ ...prev, [loadingKey]: true }));
        try {
            const result = formato === 'pdf'
                ? await downloadDanfePdf([vendaId])
                : await downloadDanfeZpl([vendaId]);

            if (result?.error) {
                const detalhes = Array.isArray(result.detalhes) && result.detalhes.length > 0
                    ? result.detalhes.join(' | ')
                    : '';
                toast({
                    title: `Erro ao gerar DANFE ${formato.toUpperCase()}`,
                    description: detalhes || result.error,
                    status: 'error',
                    duration: 6000,
                    isClosable: true
                });
            } else {
                toast({ title: `DANFE ${formato.toUpperCase()} gerada`, status: 'success', duration: 2000 });
            }
        } catch (err) {
            toast({ title: 'Erro inesperado', description: err?.message || 'Falha ao gerar DANFE.', status: 'error' });
        } finally {
            setLoadingActions(prev => ({ ...prev, [loadingKey]: false }));
        }
    };

    const handleBulkDanfePdf = async () => {
        const selectedIds = Array.from(new Set(
            selectedProducts
                .filter(p => packedStatus[p.uniqueId])
                .map(p => p.vendaId)
        ));
        const vendasComNfeSet = new Set(vendasComNfe);
        const ids = selectedIds.length > 0
            ? selectedIds.filter(id => vendasComNfeSet.has(id))
            : vendasComNfe;
        if (ids.length === 0) return toast({
            title: 'Atenção', description: 'Nenhuma venda com NFe emitida disponível.', status: 'warning'
        });
        setLoadingBulk(prev => ({ ...prev, danfePdf: true }));
        try {
            const result = await downloadDanfePdf(ids);
            if (result?.error) {
                registrarOcorrencias(result, 'DANFE PDF', () => handleBulkDanfePdf());
            } else {
                toast({ title: 'DANFEs PDF geradas', description: `${ids.length} DANFE(s) prontas.`, status: 'success', duration: 3000 });
            }
        } catch (err) {
            toast({ title: 'Erro inesperado', description: 'Falha ao baixar DANFEs PDF.', status: 'error' });
        } finally {
            setLoadingBulk(prev => ({ ...prev, danfePdf: false }));
        }
    };

    const handleBulkDanfeZpl = async () => {
        const selectedIds = Array.from(new Set(
            selectedProducts
                .filter(p => packedStatus[p.uniqueId])
                .map(p => p.vendaId)
        ));
        const vendasComNfeSet = new Set(vendasComNfe);
        const ids = selectedIds.length > 0
            ? selectedIds.filter(id => vendasComNfeSet.has(id))
            : vendasComNfe;
        if (ids.length === 0) return toast({
            title: 'Atenção', description: 'Nenhuma venda com NFe emitida disponível.', status: 'warning'
        });
        setLoadingBulk(prev => ({ ...prev, danfeZpl: true }));
        try {
            const result = await downloadDanfeZpl(ids);
            if (result?.error) {
                registrarOcorrencias(result, 'DANFE ZPL', () => handleBulkDanfeZpl());
            } else {
                toast({ title: 'DANFEs ZPL geradas', description: `${ids.length} DANFE(s) prontas.`, status: 'success', duration: 3000 });
            }
        } catch (err) {
            toast({ title: 'Erro inesperado', description: 'Falha ao baixar DANFEs ZPL.', status: 'error' });
        } finally {
            setLoadingBulk(prev => ({ ...prev, danfeZpl: false }));
        }
    };

    const fecharModalGeral = () => {
        onClose();
    };

    const temVendaIncompletaSelecionada = selectedProducts.some(p => incompleteVendaIds.has(p.vendaId));

    // Dados dos Drawers
    const editingDraftProducts = editingDraftVendaId !== null ? vendaGroups[editingDraftVendaId] : null;
    const editingDraftFirstProduct = editingDraftProducts?.[0];
    const fretePagoClienteRaw = editingDraftFirstProduct?.envio?.valor_frete;
    const fretePagoCliente = fretePagoClienteRaw === null || fretePagoClienteRaw === undefined
        ? null
        : parseFloat(fretePagoClienteRaw);
    const editingProducts = editingVendaId !== null ? vendaGroups[editingVendaId] : null;

// Contagens para botões (somente vendas que faltam processar)
    const vendasPendentesNfe = totalVendas - nfeCompleted;
    const vendasPendentesEtq = vendasComNfe.filter(vId => etiquetaStatus[vId]?.status !== 'sucesso').length;
    const productsElegiveisPacking = vendasComEtiqueta.reduce((acc, vId) => acc + (vendaGroups[vId]?.length || 0), 0);
    const packedInElegiveisCount = vendasComEtiqueta.reduce((acc, vId) => {
        return acc + (vendaGroups[vId] || []).filter(p => packedStatus[p.uniqueId]).length;
    }, 0);

    return (
        <>
            <Modal isOpen={isOpen} onClose={isRunning || isRunningEtiquetas ? null : fecharModalGeral} size={isMobile ? "full" : "6xl"} scrollBehavior="inside" trapFocus={false} blockScrollOnMount={false}>
                <ModalOverlay backdropFilter="blur(3px)" />
                <ModalContent bg="gray.50" rounded={isMobile ? "none" : "xl"} maxH={isMobile ? "100vh" : "90vh"}>
                    <ModalHeader borderBottomWidth="1px" bg="white" roundedTop={isMobile ? "none" : "xl"} py={3} px={{ base: 3, md: 6 }}>
                        <Flex justify="space-between" align="center">
                            <VStack align="flex-start" spacing={0}>
                                <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="extrabold" color="gray.800">Processamento em Lote</Text>
                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" fontWeight="normal">
                                    {totalVendas} venda{totalVendas !== 1 ? 's' : ''} • {selectedProducts.length} produto{selectedProducts.length !== 1 ? 's' : ''}
                                </Text>
                            </VStack>
                            {!isRunning && !isRunningEtiquetas && <ModalCloseButton position="static" />}
                        </Flex>
                    </ModalHeader>

                    <ModalBody p={0} bg="gray.50" display="flex" flexDirection="column" overflow="auto">
                        <Tabs
                            index={currentTabIndex}
                            onChange={(idx) => {
                                if (idx === 1 && !canGoToEtiquetas) {
                                    toast({ title: "Bloqueado", description: "Emita pelo menos uma NFe primeiro.", status: "warning", duration: 2000 });
                                    return;
                                }
                                if (idx === 2 && !canGoToControle) {
                                    toast({ title: "Bloqueado", description: "Gere pelo menos uma Etiqueta primeiro.", status: "warning", duration: 2000 });
                                    return;
                                }
                                setCurrentTabIndex(idx);
                            }}
                            colorScheme="blue"
                            isFitted
                            variant="enclosed-colored"
                        >
                            <TabList bg="white" pt={2} px={{ base: 1, md: 4 }}>
                                <Tab fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} _selected={{ color: 'blue.600', bg: 'blue.50', borderColor: 'blue.200', borderBottomColor: 'blue.50', fontWeight: 'bold' }}>
                                    <HStack spacing={1}><Icon as={FaFileInvoice} boxSize={{ base: 3, md: 4 }} /><Text display={{ base: "none", sm: "inline" }}>1.</Text><Text>NFe</Text></HStack>
                                </Tab>
                                <Tab fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} isDisabled={!canGoToEtiquetas} _selected={{ color: 'green.600', bg: 'green.50', borderColor: 'green.200', borderBottomColor: 'green.50', fontWeight: 'bold' }}>
                                    <HStack spacing={1}><Icon as={FaTruck} boxSize={{ base: 3, md: 4 }} /><Text display={{ base: "none", sm: "inline" }}>2.</Text><Text>Etiquetas</Text>
                                    {vendasComNfe.length > 0 && vendasComNfe.length < totalVendas && <Badge ml={1} colorScheme="green" fontSize="9px">{vendasComNfe.length}</Badge>}
                                    </HStack>
                                </Tab>
                                <Tab fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} isDisabled={!canGoToControle} _selected={{ color: 'purple.600', bg: 'purple.50', borderColor: 'purple.200', borderBottomColor: 'purple.50', fontWeight: 'bold' }}>
                                    <HStack spacing={1}><Icon as={FaBoxes} boxSize={{ base: 3, md: 4 }} /><Text display={{ base: "none", sm: "inline" }}>3.</Text><Text>Controle</Text>
                                    {vendasComEtiqueta.length > 0 && vendasComEtiqueta.length < totalVendas && <Badge ml={1} colorScheme="purple" fontSize="9px">{vendasComEtiqueta.length}</Badge>}
                                    </HStack>
                                </Tab>
                            </TabList>

                            <TabPanels>
                                {/* ===== ABA 1 - NFE ===== */}
                                <TabPanel p={{ base: 2, md: 4 }}>
                                    <Box bg="white" borderWidth="1px" borderColor="gray.100" p={3} rounded="lg" shadow="sm" mb={4}>
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontSize="sm" fontWeight="bold" color="gray.600">Progresso de NFes</Text>
                                            <Text fontSize="sm" fontWeight="bold" color="blue.600">{nfeProgress}%</Text>
                                        </HStack>
                                        <Progress value={nfeProgress} size="sm" colorScheme={isFinished && nfeErrors > 0 ? "red" : "blue"} rounded="full" hasStripe={isRunning} isAnimated={isRunning} mb={3} />
                                        <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={2} textAlign="center">
                                            <Box><Text fontSize="xs" color="gray.500" fontWeight="bold">Vendas</Text><Text fontSize="lg" fontWeight="bold">{totalVendas}</Text></Box>
                                            <Box><Text fontSize="xs" color="gray.500" fontWeight="bold">Progresso</Text><Text fontSize="lg" fontWeight="bold" color="orange.400">{nfeLoading}</Text></Box>
                                            <Box><Text fontSize="xs" color="gray.500" fontWeight="bold">Sucesso</Text><Text fontSize="lg" fontWeight="bold" color="green.500">{nfeCompleted}</Text></Box>
                                            <Box><Text fontSize="xs" color="gray.500" fontWeight="bold">Erros</Text><Text fontSize="lg" fontWeight="bold" color="red.500">{nfeErrors}</Text></Box>
                                        </Grid>
                                    </Box>

                                    {temVendaMl && (
                                        <Box mb={4}>
                                            <SeletorEmissorNfe
                                                valor={emissorNfe}
                                                onChange={setEmissorNfe}
                                                config={configEmissor}
                                                isDisabled={isRunning}
                                            />
                                        </Box>
                                    )}

                                    <VStack spacing={3} align="stretch" pb={4}>
                                        {vendaIds.map((vendaId) => {
                                            const products = vendaGroups[vendaId];
                                            const statusInfo = processStatus[vendaId] || { status: 'aguardando' };
                                            const st = statusInfo.status;
                                            const isIncomplete = incompleteVendaIds.has(vendaId);
                                            const isAlreadyEmitted = products.every(p => p.nfeEmitida) && st === 'sucesso';

                                            return (
                                                <Box key={`nfe-${vendaId}`} bg={isIncomplete ? "red.50" : (isAlreadyEmitted ? "green.50" : "white")} p={{ base: 2, md: 3 }} rounded="lg" shadow="sm" borderWidth="1px" borderColor={isIncomplete ? "red.300" : (st === 'erro' ? 'red.300' : (st === 'sucesso' ? 'green.300' : 'gray.200'))}>
                                                    <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "flex-start", md: "center" }} gap={2} mb={2}>
                                                        <VendaInfoHeader products={products} vendaId={vendaId} kitPacks={kitPacks} />
                                                        <HStack spacing={2} flexShrink={0} flexWrap="wrap">
                                                            {!isAlreadyEmitted && ehVendaMl(products[0]?.origem) && (
                                                                <BadgeEmissor conta={products[0]?.conta} escolha={emissorNfe} config={configEmissor} />
                                                            )}
                                                            {isAlreadyEmitted && <Badge colorScheme="green" px={2} py={0.5}><CheckCircleIcon mr={1} />Já Emitida</Badge>}
                                                            {!isAlreadyEmitted && isIncomplete && <Badge colorScheme="red"><WarningTwoIcon mr={1} />Incompleta</Badge>}
                                                            {!isAlreadyEmitted && !isIncomplete && st === 'aguardando' && <Badge colorScheme="gray"><TimeIcon mr={1} />Aguard.</Badge>}
                                                            {!isAlreadyEmitted && !isIncomplete && st === 'processando' && <Badge colorScheme="orange"><Spinner size="xs" mr={1} />Geração...</Badge>}
                                                            {!isAlreadyEmitted && !isIncomplete && st === 'sucesso' && <Badge colorScheme="green"><CheckCircleIcon mr={1} />Emitida</Badge>}
                                                            {!isAlreadyEmitted && !isIncomplete && st === 'erro' && <Badge colorScheme="red"><WarningTwoIcon mr={1} />Erro</Badge>}

                                                            {!isAlreadyEmitted && !isIncomplete && st === 'erro' && (
                                                                <IconButton icon={<EditIcon />} colorScheme="red" variant="outline" size="xs" onClick={() => handleOpenEdit(vendaId)} title="Corrigir Dados" />
                                                            )}
                                                            {!isRunning && !isFinished && onRemoveItem && (
                                                                <IconButton icon={<DeleteIcon />} colorScheme="red" variant="ghost" size="xs" onClick={() => handleRemoveVenda(vendaId)} title="Remover Venda" />
                                                            )}
                                                        </HStack>
                                                    </Flex>

                                                    <Box borderTopWidth="1px" borderColor="gray.100" pt={1}>
                                                        <VStack spacing={0} align="stretch" divider={<Divider borderColor="gray.100" />}>
                                                            {products.map((product) => (
                                                                <ProductRow key={product.uniqueId} product={product} compact={products.length > 1} />
                                                            ))}
                                                        </VStack>
                                                    </Box>

                                                    {products.some(isRetiradaLoja) && !isAlreadyEmitted && st !== 'sucesso' && (
                                                        <Box mt={2} p={2} bg="orange.50" rounded="md" borderWidth="1px" borderColor="orange.100">
                                                            <Checkbox
                                                                size="sm"
                                                                colorScheme="orange"
                                                                isChecked={!!freteCif[vendaId]}
                                                                isDisabled={isRunning || st === 'processando'}
                                                                onChange={(e) => setFreteCif(prev => ({ ...prev, [vendaId]: e.target.checked }))}
                                                            >
                                                                <Text fontSize="xs" color="orange.800" fontWeight="bold">Frete por conta da TAGA (CIF)</Text>
                                                            </Checkbox>
                                                            <Text fontSize="10px" color="gray.600" ml={6}>
                                                                Marque quando nós despachamos (Azul Cargo etc.). Retirada sai como FOB, frete por conta do cliente.
                                                            </Text>
                                                        </Box>
                                                    )}

                                                    {st === 'erro' && (
                                                        <Box mt={2} p={2} bg="red.50" rounded="md" borderWidth="1px" borderColor="red.200">
                                                            <Text fontSize="xs" color="red.700" whiteSpace="pre-wrap" overflowWrap="anywhere" wordBreak="break-word">
                                                                {statusInfo.errorMsg || 'Erro ao emitir NFe (a API não retornou detalhe).'}
                                                            </Text>
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </VStack>
                                </TabPanel>

                                {/* ===== ABA 2 - ETIQUETAS ===== */}
                                <TabPanel p={{ base: 2, md: 4 }}>
                                    <Box bg="white" borderWidth="1px" borderColor="gray.100" p={{ base: 3, md: 4 }} rounded="lg" shadow="sm" mb={4}>
                                        <HStack align="flex-start">
                                            <Icon as={FaExclamationCircle} color="blue.500" mt={0.5} />
                                            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700">
                                                Exibindo {vendasComNfe.length} venda{vendasComNfe.length !== 1 ? 's' : ''} com NFe emitida. 
                                                {vendasPendentesNfe > 0 && <Text as="span" color="orange.600" fontWeight="bold"> ({vendasPendentesNfe} venda{vendasPendentesNfe !== 1 ? 's' : ''} ainda sem NFe — volte à aba anterior)</Text>}
                                            </Text>
                                        </HStack>
                                    </Box>

                                    <VStack spacing={3} align="stretch" pb={4}>
                                        {vendasComNfe.map((vendaId) => {
                                            const products = vendaGroups[vendaId];
                                            if (!products) return null;
                                            const statusInfo = etiquetaStatus[vendaId] || { status: 'aguardando' };
                                            const st = statusInfo.status;
                                            const tipoEtq = getTipoEtiqueta(products[0]);
                                            const isME = tipoEtq === "Melhor Envio";
                                            const isLala = tipoEtq === "Lalamove";
                                            const isExterna = tipoEtq === "Externa";
                                            const isRet = tipoEtq === "Retirada";
                                            const semEtq = isLala || isExterna || isRet;
                                            const first = products[0];
                                            const draftState = draftsME[vendaId];
                                            const isAlreadyGenerated = products.every(p => p.etiquetaEmitida) && st === 'sucesso' && !semEtq;

                                            return (
                                                <Box key={`etq-${vendaId}`}
                                                    bg={
                                                        semEtq ? "purple.50"
                                                        : isAlreadyGenerated ? "green.50"
                                                        : st === 'carrinho' ? "yellow.50"
                                                        : (isME && !draftState?.isReady ? "yellow.50" : "white")
                                                    }
                                                    p={{ base: 2, md: 3 }} rounded="lg" shadow="sm" borderWidth="1px"
                                                    borderColor={
                                                        semEtq ? 'purple.300'
                                                        : st === 'sucesso' ? 'green.300'
                                                        : st === 'processando' ? 'orange.300'
                                                        : st === 'carrinho' ? 'yellow.400'
                                                        : 'gray.200'
                                                    }>
                                                    <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "flex-start", md: "center" }} gap={2} mb={2}>
                                                        <HStack spacing={2} flex={1} overflow="hidden" flexWrap="wrap">
                                                            <VendaInfoHeader products={products} vendaId={vendaId} kitPacks={kitPacks} />
                                                            <Badge colorScheme={semEtq ? "purple" : (isME ? "yellow" : "green")} fontSize="10px" display="flex" alignItems="center">
                                                                <Icon as={FaTruck} mr={1} boxSize={2.5} />{tipoEtq}
                                                            </Badge>
                                                        </HStack>

                                                        <HStack spacing={2} flexShrink={0} flexWrap="wrap">
                                                            {isLala && <Badge colorScheme="purple" px={2} py={1}><CheckCircleIcon mr={1} />Sem Etiqueta (LALAMOVE)</Badge>}
                                                            {isExterna && <Badge colorScheme="purple" px={2} py={1}><CheckCircleIcon mr={1} />Etiqueta Externa</Badge>}

                                                            {/* Retirada: no lugar do frete, as ações de balcão. */}
                                                            {isRet && (
                                                                <AcoesRetirada
                                                                    venda={first}
                                                                    onProntoRetirada={onProntoRetirada}
                                                                    onRetiradaColetada={onRetiradaColetada}
                                                                />
                                                            )}

                                                            {!semEtq && isAlreadyGenerated && <Badge colorScheme="green" px={2} py={1}><CheckCircleIcon mr={1} />Já Gerada</Badge>}
                                                            {!semEtq && !isAlreadyGenerated && st === 'sucesso' && <Badge colorScheme="green" px={2} py={1}><CheckCircleIcon mr={1} />Gerada</Badge>}
                                                            {!semEtq && !isAlreadyGenerated && st === 'processando' && <Badge colorScheme="orange" px={2} py={1}><Spinner size="xs" mr={1} />Processando...</Badge>}
                                                            {!semEtq && !isAlreadyGenerated && st === 'erro' && <Badge colorScheme="red" px={2} py={1}><WarningTwoIcon mr={1} />Erro</Badge>}
                                                            {!semEtq && !isAlreadyGenerated && st === 'carrinho' && (
                                                                <Badge colorScheme="yellow" px={2} py={1}>
                                                                    <Icon as={FaShoppingCart} mr={1} boxSize={3} />No Carrinho
                                                                </Badge>
                                                            )}

                                                            {/* Botões quando a etiqueta está SÓ no carrinho */}
                                                            {!semEtq && !isAlreadyGenerated && st === 'carrinho' && (
                                                                <>
                                                                    <Button size="xs" colorScheme="green" leftIcon={<FaBolt />}
                                                                        onClick={() => handlePagarDoCarrinho(vendaId)}
                                                                        title="Pagar e gerar etiqueta agora">
                                                                        Pagar Agora
                                                                    </Button>
                                                                    <Button size="xs" colorScheme="red" variant="outline" leftIcon={<FaTrash />}
                                                                        onClick={() => handleRemoverDoCarrinho(vendaId)}
                                                                        title="Remover do carrinho Melhor Envio">
                                                                        Remover
                                                                    </Button>
                                                                </>
                                                            )}

                                                            {!semEtq && !isAlreadyGenerated && (st === 'aguardando' || st === 'erro') && (
                                                                <>
                                                                    {!isME && st === 'aguardando' && <Badge colorScheme="green" px={2} py={1}>Pronto p/ Lote</Badge>}
                                                                    {isME && (!draftState?.isReady || st === 'erro') && (
                                                                        <Button size="xs" colorScheme="orange" leftIcon={<FaCog />} onClick={() => handleOpenDraftConfig(vendaId)}>
                                                                            {st === 'erro' ? 'Corrigir e Tentar' : 'Config. Frete'}
                                                                        </Button>
                                                                    )}
                                                                    {isME && draftState?.isReady && st !== 'erro' && (
                                                                        <Flex align="center" gap={3} borderWidth="1px" borderColor="green.200" bg="green.50" p={1.5} rounded="md">
                                                                            {draftState.companyPicture && (
                                                                                <Image src={draftState.companyPicture} w="30px" h="18px" objectFit="contain" />
                                                                            )}
                                                                            <VStack align="start" spacing={0} pr={2}>
                                                                                <Text fontWeight="bold" fontSize="10px" color="green.800">{draftState.nomeEtiqueta}</Text>
                                                                                <Text fontSize="9px" color="green.600">
                                                                                    {draftState.deliveryTime} {draftState.deliveryTime === 1 ? 'dia útil' : 'dias úteis'} • R$ {parseFloat(draftState.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                                </Text>
                                                                            </VStack>
                                                                            <IconButton icon={<EditIcon />} size="xs" variant="ghost" colorScheme="green" onClick={() => handleOpenDraftConfig(vendaId)} title="Alterar Frete" />
                                                                        </Flex>
                                                                    )}
                                                                    <Tooltip label="Marcar como envio externo (retirada, FreteNet, aéreo etc.) e pular a geração de etiqueta">
                                                                        <Button size="xs" colorScheme="purple" variant="outline" leftIcon={<FaTruck />}
                                                                            onClick={() => handleMarcarEnvioExterno(vendaId)}>
                                                                            Envio Externo
                                                                        </Button>
                                                                    </Tooltip>
                                                                </>
                                                            )}
                                                            {!isRunningEtiquetas && onRemoveItem && (
                                                                <IconButton icon={<DeleteIcon />} colorScheme="red" variant="ghost" size="xs" onClick={() => handleRemoveVenda(vendaId)} title="Remover Venda" />
                                                            )}
                                                        </HStack>
                                                    </Flex>

                                                    <Box borderTopWidth="1px" borderColor="gray.100" pt={1}>
                                                        <VStack spacing={0} align="stretch" divider={<Divider borderColor="gray.100" />}>
                                                            {products.map((product) => (
                                                                <ProductRow key={product.uniqueId} product={product} compact={products.length > 1} />
                                                            ))}
                                                        </VStack>
                                                    </Box>

                                                    {(st === 'erro' || st === 'carrinho') && statusInfo.errorMsg && (
                                                        <Box mt={2} p={2} bg="red.50" rounded="md" borderLeftWidth="4px" borderLeftColor="red.500">
                                                            <HStack>
                                                                <Icon as={WarningTwoIcon} color="red.500" size="xs" />
                                                                <Text fontSize="xs" color="red.700" fontWeight="bold">Erro na Etiqueta:</Text>
                                                            </HStack>
                                                            <Text fontSize="xs" color="red.600" mt={1} whiteSpace="normal" overflowWrap="anywhere" wordBreak="break-word">{statusInfo.errorMsg}</Text>
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </VStack>
                                </TabPanel>

                                {/* ===== ABA 3 - CONTROLE ===== */}
                                <TabPanel p={{ base: 2, md: 4 }}>
                                    {/* Header de progresso */}
                                    <Box bg="white" borderWidth="1px" borderColor="gray.100" p={3} rounded="lg" shadow="sm" mb={4}>
                                        <HStack justify="space-between" mb={2}>
                                            <VStack align="flex-start" spacing={1}>
                                                <Text fontSize="sm" fontWeight="bold" color="gray.600">Progresso de Embalagem</Text>
                                            </VStack>
                                            <Text fontSize="sm" fontWeight="bold" color="purple.600">
                                                {packedInElegiveisCount}/{productsElegiveisPacking}
                                            </Text>
                                        </HStack>
                                        <Progress value={productsElegiveisPacking > 0 ? Math.round((packedInElegiveisCount / productsElegiveisPacking) * 100) : 0} size="sm" colorScheme="purple" rounded="full" mb={3} />
                                    </Box>

                                    {/* Toggle: aplica para impressões individuais e em massa */}
                                    <Box mb={2}>
                                        <Checkbox
                                            size="sm"
                                            colorScheme="purple"
                                            isChecked={incluirDanfeNaEtiqueta}
                                            onChange={(e) => setIncluirDanfeNaEtiqueta(e.target.checked)}
                                        >
                                            <Text fontSize="sm">Incluir DANFE junto à etiqueta</Text>
                                        </Checkbox>
                                    </Box>

                                    {/* Botões de impressão em massa */}
                                    <Flex wrap="wrap" gap={2} mb={4}>
                                        <Button
                                            size="sm"
                                            leftIcon={<FaFilePdf />}
                                            colorScheme="red"
                                            variant="outline"
                                            onClick={handleBulkPrintEtiquetasPdf}
                                            isLoading={loadingBulk.pdf}
                                            isDisabled={vendasComEtiqueta.length === 0}
                                            title={vendasComEtiqueta.length === 0 ? 'Gere as etiquetas primeiro' : 'Imprimir etiquetas em PDF'}
                                        >
                                            Imprimir Etiquetas (PDF)
                                        </Button>
                                        <Button
                                            size="sm"
                                            leftIcon={<FaPrint />}
                                            colorScheme="gray"
                                            variant="outline"
                                            onClick={handleBulkPrintZpl}
                                            isLoading={loadingBulk.zpl}
                                            isDisabled={vendasComEtiqueta.length === 0}
                                            title={vendasComEtiqueta.length === 0 ? 'Gere as etiquetas primeiro' : 'Baixar etiquetas ZPL em ZIP'}
                                        >
                                            Imprimir Etiquetas (ZPL)
                                        </Button>
                                        <Button
                                            size="sm"
                                            leftIcon={<CopyIcon />}
                                            colorScheme="gray"
                                            variant="outline"
                                            onClick={handleBulkCopyZpl}
                                            isLoading={loadingBulk.copy}
                                            isDisabled={vendasComEtiqueta.length === 0}
                                            title={vendasComEtiqueta.length === 0 ? 'Gere as etiquetas primeiro' : 'Copiar todos ZPL para a área de transferência'}
                                        >
                                            Copiar ZPL (Todas)
                                        </Button>
                                        <Button
                                            size="sm"
                                            leftIcon={<FaFileInvoice />}
                                            colorScheme="purple"
                                            variant="outline"
                                            onClick={handleBulkDanfePdf}
                                            isLoading={loadingBulk.danfePdf}
                                            isDisabled={vendasComNfe.length === 0}
                                            title={vendasComNfe.length === 0 ? 'Emita as NFes primeiro' : 'Baixar DANFEs Simplificadas em PDF'}
                                        >
                                            DANFEs (PDF)
                                        </Button>
                                        <Button
                                            size="sm"
                                            leftIcon={<FaFileInvoice />}
                                            colorScheme="purple"
                                            variant="outline"
                                            onClick={handleBulkDanfeZpl}
                                            isLoading={loadingBulk.danfeZpl}
                                            isDisabled={vendasComNfe.length === 0}
                                            title={vendasComNfe.length === 0 ? 'Emita as NFes primeiro' : 'Baixar DANFEs Simplificadas em ZPL'}
                                        >
                                            DANFEs (ZPL)
                                        </Button>
                                    </Flex>

                                    {ocorrenciasImpressao && (
                                        <PainelOcorrenciasImpressao
                                            ref={painelOcorrenciasRef}
                                            ocorrencias={ocorrenciasImpressao.mensagens}
                                            titulo={ocorrenciasImpressao.titulo}
                                            parcial={ocorrenciasImpressao.parcial}
                                            onFechar={() => setOcorrenciasImpressao(null)}
                                            onTentarDeNovo={ocorrenciasImpressao.aoTentarDeNovo}
                                            tentando={loadingBulk.pdf || loadingBulk.zpl || loadingBulk.copy || loadingBulk.danfePdf || loadingBulk.danfeZpl}
                                        />
                                    )}

                                    {/* Lista de vendas */}
                                    <Box mb={3} px={1}>
                                        <Checkbox
                                            colorScheme="green"
                                            size="lg"
                                            background="white"
                                            px={3}
                                            py={2}
                                            rounded="md"
                                            shadow="sm"
                                            borderWidth="1px"
                                            w="auto"
                                            isChecked={productsElegiveisPacking > 0 && packedInElegiveisCount === productsElegiveisPacking}
                                            isIndeterminate={packedInElegiveisCount > 0 && packedInElegiveisCount < productsElegiveisPacking}
                                            onChange={handleSelectAllPacked}
                                        >
                                            <Text fontWeight="bold" color="gray.700">Selecionar Todas as Vendas</Text>
                                        </Checkbox>
                                    </Box>
                                    <VStack spacing={3} align="stretch" pb={4}>
                                        {vendasComEtiqueta.map((vendaId) => {
                                            const products = vendaGroups[vendaId];
                                            if (!products) return null;
                                            const allProductsPacked = products.every(p => packedStatus[p.uniqueId]);
                                            const pendencia = motivosImpressaoPorVenda[vendaId];

                                            return (
                                                <Box key={`ctrl-${vendaId}`} bg={allProductsPacked ? "green.50" : "white"} p={{ base: 2, md: 3 }} rounded="lg" shadow="sm" borderWidth="1px" borderColor={allProductsPacked ? "green.400" : "gray.200"}>
                                                    <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "flex-start", md: "center" }} gap={2} mb={2}>
                                                        <VendaInfoHeader products={products} vendaId={vendaId} kitPacks={kitPacks} />
                                                        <HStack spacing={1} flexShrink={0} flexWrap="wrap">
                                                            <Button size="xs" leftIcon={<FaFilePdf />} variant="ghost" colorScheme="red" onClick={() => handlePrintPdf(vendaId)} isLoading={loadingActions[`${vendaId}-pdf`]}>
                                                                PDF
                                                            </Button>
                                                            <Button size="xs" leftIcon={<FaPrint />} variant="ghost" colorScheme="gray" onClick={() => handlePrintZpl(vendaId)} isLoading={loadingActions[`${vendaId}-zpl`]}>
                                                                ZPL
                                                            </Button>
                                                            <Button size="xs" leftIcon={<CopyIcon />} variant="ghost" colorScheme="gray" onClick={() => handleCopyZpl(vendaId)} isLoading={loadingActions[`${vendaId}-copy`]}>
                                                                ZPL
                                                            </Button>
                                                            <HStack spacing={0}>
                                                                <Button size="xs" leftIcon={<FaFileInvoice />} variant="ghost" colorScheme="blue" onClick={() => handlePrintNfe(vendaId, 'xml')} isLoading={loadingActions[`${vendaId}-nfe-xml`]} px={1}>
                                                                    NF-e (XML)
                                                                </Button>
                                                            </HStack>
                                                            <Button size="xs" leftIcon={<FaFileInvoice />} variant="ghost" colorScheme="purple" onClick={() => handleIndividualDanfe(vendaId, 'pdf')} isLoading={loadingActions[`${vendaId}-danfe-pdf`]}>
                                                                DANFE PDF
                                                            </Button>
                                                            <Button size="xs" leftIcon={<FaFileInvoice />} variant="ghost" colorScheme="purple" onClick={() => handleIndividualDanfe(vendaId, 'zpl')} isLoading={loadingActions[`${vendaId}-danfe-zpl`]}>
                                                                DANFE ZPL
                                                            </Button>
                                                            {allProductsPacked && (
                                                                <Badge colorScheme="green" px={2} py={1}><CheckCircleIcon mr={1} />Embalada</Badge>
                                                            )}
                                                            {isRetiradaLoja(products[0]) && (
                                                                <AcoesRetirada
                                                                    venda={products[0]}
                                                                    onProntoRetirada={onProntoRetirada}
                                                                    onRetiradaColetada={onRetiradaColetada}
                                                                />
                                                            )}
                                                            {onRemoveItem && (
                                                                <IconButton icon={<DeleteIcon />} colorScheme="red" variant="ghost" size="xs" onClick={() => handleRemoveVenda(vendaId)} title="Remover Venda" />
                                                            )}
                                                        </HStack>
                                                    </Flex>

                                                    {pendencia && (
                                                        <Box mb={2} p={2} bg={`${pendencia.cor}.50`} rounded="md" borderLeftWidth="4px" borderLeftColor={`${pendencia.cor}.400`}>
                                                            <HStack spacing={2} align="flex-start">
                                                                <Icon as={WarningTwoIcon} color={`${pendencia.cor}.500`} mt="2px" boxSize={3} />
                                                                <Box minW={0}>
                                                                    <Text fontSize="xs" fontWeight="bold" color={`${pendencia.cor}.700`}>
                                                                        Não imprimiu: {pendencia.titulo}
                                                                    </Text>
                                                                    <Text fontSize="xs" color="gray.700" mt={0.5} overflowWrap="anywhere">
                                                                        {pendencia.motivo}
                                                                    </Text>
                                                                    <Text fontSize="2xs" color={`${pendencia.cor}.700`} mt={1} fontWeight="medium">
                                                                        {pendencia.acao}
                                                                    </Text>
                                                                </Box>
                                                            </HStack>
                                                        </Box>
                                                    )}

                                                    <Box borderTopWidth="1px" borderColor="gray.100" pt={1}>
                                                        <VStack spacing={0} align="stretch" divider={<Divider borderColor="gray.100" />}>
                                                            {products.map((product) => {
                                                                const isPacked = packedStatus[product.uniqueId] || false;
                                                                const isAlreadyPacked = (product.verificationDetails?.status === 3) && !localReverted.has(product.uniqueId);
                                                                return (
                                                                    <HStack 
                                                                        key={product.uniqueId} 
                                                                        spacing={3} 
                                                                        py={2} 
                                                                        px={1} 
                                                                        cursor="pointer"
                                                                        _hover={{ bg: isPacked ? 'green.100' : 'gray.50' }}
                                                                        rounded="md"
                                                                        transition="background 0.15s"
                                                                        onClick={() => handleTogglePacked(product)}
                                                                    >
                                                                        <Checkbox
                                                                            isChecked={isPacked}
                                                                            onChange={() => handleTogglePacked(product)}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            colorScheme="green"
                                                                            size="lg"
                                                                        />
                                                                        <Box flex={1} minW={0} opacity={isPacked ? 0.6 : 1}>
                                                                            <ProductRow product={product} compact={products.length > 1} />
                                                                        </Box>
                                                                        {isPacked && isAlreadyPacked && (
                                                                            <VStack spacing={0.5} flexShrink={0}>
                                                                                <Badge colorScheme="green" fontSize="xs">✓ Já Embalado</Badge>
                                                                                <Button size="xs" variant="ghost" colorScheme="orange" onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    if (onRevertPacked) {
                                                                                        onRevertPacked([product]);
                                                                                        setPackedStatus(prev => ({ ...prev, [product.uniqueId]: false }));
                                                                                        setLocalReverted(prev => {
                                                                                            const next = new Set(prev);
                                                                                            next.add(product.uniqueId);
                                                                                            return next;
                                                                                        });
                                                                                    }
                                                                                }}>
                                                                                    Desembalar
                                                                                </Button>
                                                                            </VStack>
                                                                        )}
                                                                        {isPacked && !isAlreadyPacked && (
                                                                            <Badge colorScheme="green" fontSize="xs" flexShrink={0}>✓ Embalado</Badge>
                                                                        )}
                                                                    </HStack>
                                                                );
                                                            })}
                                                        </VStack>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </VStack>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </ModalBody>

                    <ModalFooter bg="white" borderTopWidth="1px" roundedBottom={isMobile ? "none" : "xl"} px={{ base: 3, md: 6 }}>
                        {currentTabIndex === 0 && (
                            <Flex w="100%" direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "stretch", md: "center" }} gap={3}>
                                {temVendaIncompletaSelecionada ? (
                                    <Text color="red.600" fontSize={{ base: "xs", md: "sm" }} fontWeight="bold">⚠️ Remova vendas incompletas para emitir NFe</Text>
                                ) : (
                                    <Box />
                                )}
                                <HStack spacing={2} justify={{ base: "flex-end", md: "flex-end" }} flexWrap="wrap">
                                    <Button variant="ghost" onClick={fecharModalGeral} size={{ base: "sm", md: "md" }}>Cancelar</Button>
                                    {nfePending > 0 || nfeErrors > 0 ? (
                                        nfeErrors > 0 && isFinished ? (
                                            <Button colorScheme="red" size={{ base: "sm", md: "md" }} onClick={reprocessarErrosNfe} shadow="md">Reprocessar Erros</Button>
                                        ) : (
                                            <Button colorScheme="blue" size={{ base: "sm", md: "md" }} onClick={iniciarProcessamentoNfe} isDisabled={totalVendas === 0 || temVendaIncompletaSelecionada || isRunning || vendasPendentesNfe === 0} isLoading={isRunning} shadow="md">
                                                Emitir NFe ({vendasPendentesNfe})
                                            </Button>
                                        )
                                    ) : (
                                        <Button colorScheme="green" size={{ base: "sm", md: "md" }} rightIcon={<CheckCircleIcon />} onClick={() => setCurrentTabIndex(1)} shadow="md">Avançar p/ Etiquetas</Button>
                                    )}
                                </HStack>
                            </Flex>
                        )}

                        {currentTabIndex === 1 && (
                            <Flex w="100%" direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "stretch", md: "center" }} gap={3}>
                                <Button variant="ghost" size={{ base: "sm", md: "md" }} onClick={() => setCurrentTabIndex(0)}>Voltar p/ NFes</Button>
                                <HStack spacing={2} justify="flex-end" flexWrap="wrap">
                                    {vendasPendentesEtq > 0 ? (
                                        <Tooltip label={!canPrintMassLabels ? "Configure os fretes pendentes do Melhor Envio!" : ""} placement="top">
                                            <Button colorScheme="green" size={{ base: "sm", md: "md" }} onClick={iniciarProcessamentoEtiquetas} isDisabled={!canPrintMassLabels || isRunningEtiquetas} isLoading={isRunningEtiquetas} shadow="md">
                                                Gerar Etiquetas ({vendasPendentesEtq})
                                            </Button>
                                        </Tooltip>
                                    ) : canGoToControle ? (
                                        <Button colorScheme="purple" size={{ base: "sm", md: "md" }} rightIcon={<CheckCircleIcon />} onClick={() => setCurrentTabIndex(2)} shadow="md">Avançar p/ Controle</Button>
                                    ) : (
                                        <Badge colorScheme="green" px={3} py={2} fontSize="sm">Todas etiquetas geradas ✓</Badge>
                                    )}
                                </HStack>
                            </Flex>
                        )}

                        {currentTabIndex === 2 && (
                            <Flex w="100%" direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "stretch", md: "center" }} gap={3}>
                                <Button variant="ghost" size={{ base: "sm", md: "md" }} onClick={() => setCurrentTabIndex(1)}>Voltar p/ Etiquetas</Button>
                                <HStack spacing={2} justify="flex-end">
                                    <Button variant="ghost" size={{ base: "sm", md: "md" }} onClick={fecharModalGeral}>Fechar</Button>
                                    <Tooltip label={packedInElegiveisCount === 0 ? "Embale pelo menos um produto para concluir" : ""} placement="top">
                                        <Button colorScheme="green" size={{ base: "sm", md: "lg" }} onClick={handleConcluirControle} isDisabled={packedInElegiveisCount === 0} shadow="md" leftIcon={<FaBoxes />}>
                                            Concluir ({packedInElegiveisCount}/{productsElegiveisPacking})
                                        </Button>
                                    </Tooltip>
                                </HStack>
                            </Flex>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* DRAWER - CORREÇÃO NFE */}
            <Drawer isOpen={Boolean(editingVendaId)} placement="right" onClose={handleCloseEdit} size={isMobile ? "full" : "lg"} blockScrollOnMount={false} trapFocus={false} useInert={false}>
                <DrawerOverlay bg="blackAlpha.300" />
                <DrawerContent overflowY="auto" onWheel={(e) => e.stopPropagation()}>
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth="1px" bg="blue.700" color="white" pt={6}>
                        <VStack align="flex-start" spacing={1}>
                            <HStack>
                                <Icon as={FaExclamationCircle} />
                                <Text fontSize="lg" fontWeight="bold">Correção de Dados — NFe</Text>
                            </HStack>
                            <Text fontSize="xs" fontWeight="normal" opacity={0.85}>Venda #{editingProducts?.[0]?.orderId || editingVendaId}</Text>
                        </VStack>
                    </DrawerHeader>
                    <DrawerBody bg="gray.50" p={{ base: 3, md: 5 }} overflowY="auto">
                        {editingPayload && (
                            <VStack spacing={4} align="stretch" pb={10}>

                                {/* Erros de validação */}
                                {editingPayload.validationErrors?.length > 0 && (
                                    <Box bg="red.50" border="1px solid" borderColor="red.200" rounded="lg" p={3}>
                                        <HStack mb={2}><Icon as={FaExclamationCircle} color="red.500" /><Text fontWeight="bold" fontSize="sm" color="red.700">Campos com erro:</Text></HStack>
                                        <VStack align="stretch" spacing={1}>
                                            {editingPayload.validationErrors.map((err, i) => (
                                                <Text key={i} fontSize="xs" color="red.600">• {err}</Text>
                                            ))}
                                        </VStack>
                                    </Box>
                                )}

                                {/* Número do Pedido */}
                                <Box bg="white" shadow="sm" p={4} rounded="xl" borderWidth="1px">
                                    <Text fontWeight="bold" color="gray.700" mb={3} fontSize="sm">Pedido</Text>
                                    <FormControl isRequired>
                                        <FormLabel fontSize="xs">Número do Pedido (orderNumber)</FormLabel>
                                        <Input
                                            size="sm"
                                            value={editingPayload.orderNumber}
                                            onChange={(e) => setEditingPayload(p => ({ ...p, orderNumber: e.target.value }))}
                                            placeholder="Ex: 12345"
                                            borderColor={!editingPayload.orderNumber ? 'red.400' : 'gray.200'}
                                        />
                                    </FormControl>
                                </Box>

                                {/* Dados do Cliente */}
                                <Box bg="white" shadow="sm" p={4} rounded="xl" borderWidth="1px">
                                    <Text fontWeight="bold" color="gray.700" mb={3} fontSize="sm">Dados do Comprador</Text>
                                    <VStack spacing={3}>
                                        <FormControl>
                                            <FormLabel fontSize="xs">Nome Completo</FormLabel>
                                            <Input size="sm" value={editingPayload.customer.name} onChange={(e) => setEditingPayload(p => ({ ...p, customer: { ...p.customer, name: e.target.value } }))} />
                                        </FormControl>
                                        <Grid templateColumns="1fr 1fr" gap={3} w="100%">
                                            <FormControl isRequired>
                                                <FormLabel fontSize="xs">Tipo de Pessoa</FormLabel>
                                                <select
                                                    style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: '6px', padding: '6px 8px', fontSize: '14px' }}
                                                    value={editingPayload.customer.userType}
                                                    onChange={(e) => setEditingPayload(p => ({ ...p, customer: { ...p.customer, userType: e.target.value } }))}
                                                >
                                                    <option value="INDIVIDUAL">Pessoa Física (CPF)</option>
                                                    <option value="LEGAL">Pessoa Jurídica (CNPJ)</option>
                                                </select>
                                            </FormControl>
                                            <FormControl isRequired>
                                                <FormLabel fontSize="xs">
                                                    {editingPayload.customer.userType === 'INDIVIDUAL' ? 'CPF' : 'CNPJ'}
                                                </FormLabel>
                                                <Input
                                                    size="sm"
                                                    value={editingPayload.customer.userType === 'INDIVIDUAL' ? editingPayload.customer.cpf : editingPayload.customer.cnpj}
                                                    onChange={(e) => {
                                                        const field = editingPayload.customer.userType === 'INDIVIDUAL' ? 'cpf' : 'cnpj';
                                                        setEditingPayload(p => ({ ...p, customer: { ...p.customer, [field]: e.target.value } }));
                                                    }}
                                                    placeholder={editingPayload.customer.userType === 'INDIVIDUAL' ? '000.000.000-00' : '00.000.000/0000-00'}
                                                    borderColor={
                                                        (editingPayload.customer.userType === 'INDIVIDUAL' && !editingPayload.customer.cpf) ||
                                                        (editingPayload.customer.userType === 'LEGAL' && !editingPayload.customer.cnpj)
                                                            ? 'red.400' : 'gray.200'
                                                    }
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid templateColumns="1fr 1fr" gap={3} w="100%">
                                            <FormControl>
                                                <FormLabel fontSize="xs">E-mail</FormLabel>
                                                <Input size="sm" value={editingPayload.customer.email} onChange={(e) => setEditingPayload(p => ({ ...p, customer: { ...p.customer, email: e.target.value } }))} placeholder="cliente@email.com" />
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel fontSize="xs">Telefone</FormLabel>
                                                <Input size="sm" value={editingPayload.customer.phone} onChange={(e) => setEditingPayload(p => ({ ...p, customer: { ...p.customer, phone: e.target.value } }))} placeholder="(11) 99999-9999" />
                                            </FormControl>
                                        </Grid>
                                        {editingPayload.customer.userType === 'LEGAL' && (
                                            <FormControl>
                                                <FormLabel fontSize="xs">Inscrição Estadual (IE)</FormLabel>
                                                <Input
                                                    size="sm"
                                                    value={editingPayload.customer.ie || ''}
                                                    onChange={(e) => setEditingPayload(p => ({ ...p, customer: { ...p.customer, ie: e.target.value } }))}
                                                    placeholder="Somente números ou ISENTO"
                                                />
                                            </FormControl>
                                        )}
                                    </VStack>
                                </Box>

                                {/* Endereço de Entrega */}
                                <Box bg="white" shadow="sm" p={4} rounded="xl" borderWidth="1px">
                                    <Text fontWeight="bold" color="gray.700" mb={3} fontSize="sm">Endereço de Entrega</Text>
                                    <VStack spacing={3}>
                                        <Grid templateColumns="1fr 2fr" gap={3} w="100%">
                                            <FormControl isRequired>
                                                <FormLabel fontSize="xs">CEP</FormLabel>
                                                <Input
                                                    size="sm"
                                                    value={editingPayload.shippingAddress.zipCode}
                                                    onChange={(e) => setEditingPayload(p => ({ ...p, shippingAddress: { ...p.shippingAddress, zipCode: e.target.value } }))}
                                                    placeholder="00000-000"
                                                    borderColor={!editingPayload.shippingAddress.zipCode ? 'red.400' : 'gray.200'}
                                                />
                                            </FormControl>
                                            <FormControl isRequired>
                                                <FormLabel fontSize="xs">Logradouro (Rua/Av)</FormLabel>
                                                <Input
                                                    size="sm"
                                                    value={editingPayload.shippingAddress.street}
                                                    onChange={(e) => setEditingPayload(p => ({ ...p, shippingAddress: { ...p.shippingAddress, street: e.target.value } }))}
                                                    placeholder="Rua das Flores"
                                                    borderColor={!editingPayload.shippingAddress.street ? 'red.400' : 'gray.200'}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid templateColumns="1fr 2fr" gap={3} w="100%">
                                            <FormControl>
                                                <FormLabel fontSize="xs">Número</FormLabel>
                                                <Input size="sm" value={editingPayload.shippingAddress.number} onChange={(e) => setEditingPayload(p => ({ ...p, shippingAddress: { ...p.shippingAddress, number: e.target.value } }))} placeholder="123" />
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel fontSize="xs">Complemento</FormLabel>
                                                <Input size="sm" value={editingPayload.shippingAddress.complement} onChange={(e) => setEditingPayload(p => ({ ...p, shippingAddress: { ...p.shippingAddress, complement: e.target.value } }))} placeholder="Apto 10" />
                                            </FormControl>
                                        </Grid>
                                        <FormControl isRequired>
                                            <FormLabel fontSize="xs">Bairro</FormLabel>
                                            <Input
                                                size="sm"
                                                value={editingPayload.shippingAddress.neighborhood}
                                                onChange={(e) => setEditingPayload(p => ({ ...p, shippingAddress: { ...p.shippingAddress, neighborhood: e.target.value } }))}
                                                placeholder="Centro"
                                                borderColor={!editingPayload.shippingAddress.neighborhood ? 'red.400' : 'gray.200'}
                                            />
                                        </FormControl>
                                        <Grid templateColumns="2fr 1fr" gap={3} w="100%">
                                            <FormControl isRequired>
                                                <FormLabel fontSize="xs">Cidade</FormLabel>
                                                <Input
                                                    size="sm"
                                                    value={editingPayload.shippingAddress.city}
                                                    onChange={(e) => setEditingPayload(p => ({ ...p, shippingAddress: { ...p.shippingAddress, city: e.target.value } }))}
                                                    placeholder="São Paulo"
                                                    borderColor={!editingPayload.shippingAddress.city ? 'red.400' : 'gray.200'}
                                                />
                                            </FormControl>
                                            <FormControl isRequired>
                                                <FormLabel fontSize="xs">Estado (UF)</FormLabel>
                                                <Input
                                                    size="sm"
                                                    value={editingPayload.shippingAddress.state}
                                                    onChange={(e) => setEditingPayload(p => ({ ...p, shippingAddress: { ...p.shippingAddress, state: e.target.value.toUpperCase().slice(0, 2) } }))}
                                                    placeholder="SP"
                                                    maxLength={2}
                                                    borderColor={!editingPayload.shippingAddress.state ? 'red.400' : 'gray.200'}
                                                />
                                            </FormControl>
                                        </Grid>
                                    </VStack>
                                </Box>

                            </VStack>
                        )}
                    </DrawerBody>
                    <DrawerFooter borderTopWidth="1px" bg="white">
                        <Button variant="outline" mr={3} onClick={handleCloseEdit}>Cancelar</Button>
                        <Button colorScheme="blue" leftIcon={<Icon as={FaFileInvoice} />} onClick={handleSaveEdit}>Reprocessar NFe</Button>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>

            {/* DRAWER - MELHOR ENVIO */}
            <Drawer isOpen={editingDraftVendaId !== null} placement="right" onClose={handleCloseDraftConfig} size={{ base: "full", lg: "xl", xl: "xl" }} blockScrollOnMount={false} trapFocus={false} useInert={false}>
                <DrawerOverlay bg="blackAlpha.300" />
                <DrawerContent overflowY="auto" onWheel={(e) => e.stopPropagation()}>
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth="1px" bg="yellow.500" color="white" pt={6}>
                        <VStack align="flex-start" spacing={1}>
                            <HStack><Icon as={FaTruck} /><Text fontSize="lg" fontWeight="bold">Configurar Melhor Envio</Text></HStack>
                            <Text fontSize="xs" fontWeight="normal">Venda {editingDraftFirstProduct?.orderId || editingDraftVendaId} • Confirme os dados</Text>
                        </VStack>
                    </DrawerHeader>

                    <DrawerBody bg="gray.100" p={{ base: 3, md: 5 }}>
                        {isDraftMeLoading && !draftMePayload ? (
                            <Flex justify="center" align="center" h="100%" direction="column">
                                <Spinner size="xl" color="yellow.500" mb={4} />
                                <Text color="gray.600">Carregando dados do envio...</Text>
                            </Flex>
                        ) : draftMeError ? (
                            <Alert status="error" borderRadius="md">
                                <AlertIcon />
                                {draftMeError}
                            </Alert>
                        ) : draftMePayload ? (
                            <VStack spacing={4} align="stretch" pb={10}>
                                {editingDraftProducts && editingDraftProducts.length > 0 && (
                                    <Box bg="white" p={{ base: 3, md: 4 }} rounded="md" shadow="sm" borderWidth="1px">
                                        <Text fontWeight="bold" fontSize="md" mb={3} color="purple.700">Produtos da Venda ({editingDraftProducts.length})</Text>
                                        <VStack spacing={2} align="stretch">
                                            {editingDraftProducts.map(p => (
                                                <HStack key={p.uniqueId} spacing={2}>
                                                    <ProdutoImagem produto={p.produtoEstoque} origem={null} width={"30px"} height={"30px"} imagemAPI={p.imageUrl} />
                                                    <Text fontSize="xs" color="gray.700" isTruncated flex={1}>{p.name}</Text>
                                                    {p.quantity > 1 && <Badge colorScheme="blue" fontSize="xs">x{p.quantity}</Badge>}
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>
                                )}

                                <Box bg="white" p={{ base: 3, md: 4 }} rounded="md" shadow="sm" borderWidth="1px">
                                    <Text fontWeight="bold" fontSize="md" mb={3} color="yellow.700">1. Dimensões do Pacote (Volumes)</Text>
                                    {draftMePayload.volumes.map((vol, idx) => (
                                        <Box key={idx} mb={3} p={3} bg="gray.50" borderRadius="md" border="1px dashed" borderColor="gray.300">
                                            <Flex direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "stretch", sm: "center" }} mb={3} gap={2}>
                                                <FormControl w="full" maxW={{ base: "full", sm: "300px" }}>
                                                    <Input size="sm" type="text" bg="white" value={vol._nome || ''} onChange={(e) => handleVolumeChange(idx, '_nome', e.target.value)} placeholder="Nome do pacote" fontWeight="bold" />
                                                </FormControl>
                                                {draftMePayload.volumes.length > 1 && (
                                                    <Button size="xs" colorScheme="red" variant="ghost" alignSelf={{ base: "flex-end", sm: "auto" }} onClick={() => handleRemoveVolume(idx)}>Remover</Button>
                                                )}
                                            </Flex>
                                            <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(5, 1fr)" }} gap={3}>
                                                <FormControl><FormLabel fontSize="xs">Peso (kg)</FormLabel><Input size="sm" bg="white" value={vol.weight} onChange={(e) => handleVolumeChange(idx, 'weight', e.target.value)} /></FormControl>
                                                <FormControl><FormLabel fontSize="xs">Alt. (cm)</FormLabel><Input size="sm" bg="white" value={vol.height} onChange={(e) => handleVolumeChange(idx, 'height', e.target.value)} /></FormControl>
                                                <FormControl><FormLabel fontSize="xs">Larg. (cm)</FormLabel><Input size="sm" bg="white" value={vol.width} onChange={(e) => handleVolumeChange(idx, 'width', e.target.value)} /></FormControl>
                                                <FormControl><FormLabel fontSize="xs">Comp. (cm)</FormLabel><Input size="sm" bg="white" value={vol.length} onChange={(e) => handleVolumeChange(idx, 'length', e.target.value)} /></FormControl>
                                                <FormControl><FormLabel fontSize="xs">Seguro (R$)</FormLabel><Input size="sm" bg="white" value={vol._valor || ''} onChange={(e) => handleVolumeChange(idx, '_valor', e.target.value)} /></FormControl>
                                            </Grid>
                                            {vol._estimado && (
                                                <Text mt={2} fontSize="xs" color="gray.500">
                                                    <Text as="span" fontWeight="bold" color="gray.600">Dimensões estimadas:</Text>{' '}
                                                    Peso {formatarEstimativa(vol._estimado.weight)} kg · Alt. {formatarEstimativa(vol._estimado.height)} · Larg. {formatarEstimativa(vol._estimado.width)} · Comp. {formatarEstimativa(vol._estimado.length)} cm
                                                </Text>
                                            )}
                                        </Box>
                                    ))}
                                    <Flex direction={{ base: "column", sm: "row" }} justify="space-between" align="center" gap={2} mb={2}>
                                        <Button size="xs" colorScheme="blue" variant="outline" w={{ base: "full", sm: "auto" }} onClick={handleAddVolume}>+ Adicionar Outro Pacote</Button>
                                        <Button size="xs" colorScheme="green" leftIcon={<FaSave />} w={{ base: "full", sm: "auto" }} isLoading={salvandoDimensoes} onClick={handleSalvarDimensoes}>Salvar dimensões</Button>
                                    </Flex>
                                </Box>

                                <Box bg="white" p={{ base: 3, md: 4 }} rounded="md" shadow="sm" borderWidth="1px">
                                    {renderResumoDestinatario()}
                                    {renderBannerFreteOriginal()}
                                    {renderBannerTransportadoraCombinada()}
                                    <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "flex-start", md: "center" }} mb={3} gap={2}>
                                        <Text fontWeight="bold" fontSize="md" color="yellow.700">2. Serviço de Frete</Text>
                                        <Button size="sm" colorScheme="green" onClick={handleCotarFrete} isLoading={isDraftMeLoading}>Confirmar e Cotar Frete</Button>
                                    </Flex>
                                    
                                    {draftMeCotacoes.length > 0 ? (
                                        <VStack align="stretch" spacing={2}>
                                            {draftMeCotacoes.slice().sort((a, b) => {
                                                const aDest = verificarDestaque(a);
                                                const bDest = verificarDestaque(b);
                                                if (aDest && !bDest) return -1;
                                                if (!aDest && bDest) return 1;
                                                return parseFloat(a.price) - parseFloat(b.price);
                                            }).map((cotacao) => {
                                                const isDestacado = verificarDestaque(cotacao);
                                                const divergencia = calcularDivergenciaFrete(cotacao.price, fretePagoCliente, limitesAlertaFrete);
                                                const alerta = !!divergencia?.acimaDoLimite;
                                                return (
                                                <Box key={cotacao.id} p={3} borderWidth={alerta ? "2px" : "1px"} borderColor={alerta ? "red.400" : isDestacado ? "green.400" : "gray.200"} rounded="md" bg={alerta ? "red.50" : isDestacado ? "green.50" : "white"} _hover={{ bg: alerta ? 'red.100' : isDestacado ? 'green.100' : 'blue.50' }}>
                                                    <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                                                        <Flex align="center" gap={3}>
                                                            {cotacao.company?.picture && (
                                                                <Image src={cotacao.company.picture} alt={cotacao.company.name} w="40px" h="24px" objectFit="contain" />
                                                            )}
                                                            <VStack align="start" spacing={0}>
                                                                <HStack flexWrap="wrap" gap={1}>
                                                                    <Text fontWeight="bold" fontSize="sm">{cotacao.name}</Text>
                                                                    {isDestacado && <Badge colorScheme="green" fontSize="0.6rem">Original da Venda</Badge>}
                                                                    {alerta && <Badge colorScheme="red" fontSize="0.6rem">Consultar gestão</Badge>}
                                                                </HStack>
                                                                <Text fontSize="xs" color="gray.500">{cotacao.delivery_time} {cotacao.delivery_time === 1 ? 'dia útil' : 'dias úteis'}</Text>
                                                            </VStack>
                                                        </Flex>
                                                        <Flex align="center" gap={3} wrap="wrap">
                                                            <VStack align={{ base: "start", sm: "end" }} spacing={0}>
                                                                <Text fontWeight="extrabold" fontSize="md" color={alerta ? "red.600" : "green.600"}>R$ {parseFloat(cotacao.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</Text>
                                                                {divergencia && (
                                                                    <Text fontSize="xs" fontWeight="bold" color={alerta ? "red.600" : divergencia.maisCaro ? "orange.600" : "green.600"} whiteSpace="nowrap">
                                                                        {divergencia.maisCaro ? '+' : '−'}{formatarReais(Math.abs(divergencia.diferenca))} ({divergencia.maisCaro ? '+' : '−'}{Math.abs(divergencia.percentual).toFixed(0)}%)
                                                                        <Text as="span" color="gray.500" fontWeight="normal"> vs pago</Text>
                                                                    </Text>
                                                                )}
                                                            </VStack>
                                                            <Button size="sm" colorScheme="green" onClick={() => confirmarMelhorEnvioDraft(cotacao)} title="Salvar rascunho — será gerada junto com as outras em lote">
                                                                Escolher (Lote)
                                                            </Button>
                                                            <Button size="sm" colorScheme="yellow" variant="outline" leftIcon={<FaShoppingCart />}
                                                                onClick={() => handleAdicionarAoCarrinhoDoDrawer(cotacao)}
                                                                title="Adicionar apenas ao carrinho Melhor Envio (não gera agora)">
                                                                Carrinho
                                                            </Button>
                                                        </Flex>
                                                    </Flex>
                                                    {alerta && (
                                                        <Flex align="center" gap={2} mt={2} pt={2} borderTopWidth="1px" borderTopColor="red.200">
                                                            <Icon as={FaExclamationCircle} color="red.500" boxSize={3.5} flexShrink={0} />
                                                            <Text fontSize="xs" color="red.700">
                                                                Custa {formatarReais(divergencia.diferenca)} a mais do que o cliente pagou, acima do limite de {limitesAlertaFrete.percentual}% e {formatarReais(limitesAlertaFrete.valor)}. Consulte a gestão antes de escolher.
                                                            </Text>
                                                        </Flex>
                                                    )}
                                                </Box>
                                            )})}

                                        </VStack>
                                    ) : (
                                        <Text fontSize="sm" color="gray.500" fontStyle="italic">Nenhuma cotação gerada ainda. Atualize o peso/CEP e clique em Cotar.</Text>
                                    )}
                                </Box>

                                <Box bg="white" rounded="md" shadow="sm" borderWidth="1px" overflow="hidden">
                                    <Flex
                                        as="button"
                                        type="button"
                                        w="100%"
                                        px={{ base: 3, md: 4 }}
                                        py={3}
                                        align="center"
                                        justify="space-between"
                                        cursor="pointer"
                                        onClick={() => setShowDestinatario(prev => !prev)}
                                        _hover={{ bg: 'gray.50' }}
                                        transition="background 0.15s"
                                    >
                                        <HStack spacing={3}>
                                            <Icon as={FaUser} color="gray.500" />
                                            <Text fontWeight="bold" fontSize="md" color="gray.700">Editar Endereço do Destinatário</Text>
                                            {!showDestinatario && (
                                                <Badge colorScheme="gray" variant="subtle" fontSize="0.65rem">opcional</Badge>
                                            )}
                                        </HStack>
                                        <Icon as={showDestinatario ? ChevronUpIcon : ChevronDownIcon} boxSize={5} color="gray.500" />
                                    </Flex>

                                    <Collapse in={showDestinatario} animateOpacity>
                                        <Box px={{ base: 3, md: 4 }} pb={4} pt={1} borderTop="1px solid" borderTopColor="gray.100">
                                            <Grid templateColumns={{ base: "1fr", md: "repeat(12, 1fr)" }} gap={3}>
                                                <GridItem colSpan={{ base: 12, md: 8 }}>
                                                    <FormControl isRequired>
                                                        <FormLabel fontSize="xs" color="gray.600">Nome Completo</FormLabel>
                                                        <Input size="sm" value={draftMePayload.to?.name || ''} onChange={(e) => handleToChange('name', e.target.value)} />
                                                    </FormControl>
                                                </GridItem>
                                                <GridItem colSpan={{ base: 12, md: 4 }}>
                                                    <FormControl isRequired>
                                                        <FormLabel fontSize="xs" color="gray.600">CEP</FormLabel>
                                                        <Input size="sm" value={draftMePayload.to?.postal_code || ''} onChange={(e) => handleToChange('postal_code', e.target.value)} />
                                                    </FormControl>
                                                </GridItem>
                                                <GridItem colSpan={{ base: 12, md: 6 }}>
                                                    <FormControl isRequired>
                                                        <FormLabel fontSize="xs" color="gray.600">CPF / CNPJ</FormLabel>
                                                        <Input size="sm" value={draftMePayload.to?.document || draftMePayload.to?.company_document || ''} onChange={(e) => handleToChange('document', e.target.value)} />
                                                    </FormControl>
                                                </GridItem>
                                                <GridItem colSpan={{ base: 12, md: 6 }}>
                                                    <FormControl isRequired>
                                                        <FormLabel fontSize="xs" color="gray.600">Telefone (DDD+Num)</FormLabel>
                                                        <Input size="sm" value={draftMePayload.to?.phone || ''} onChange={(e) => handleToChange('phone', e.target.value)} />
                                                    </FormControl>
                                                </GridItem>
                                                <GridItem colSpan={{ base: 12, md: 9 }}>
                                                    <FormControl isRequired>
                                                        <FormLabel fontSize="xs" color="gray.600">Rua / Endereço</FormLabel>
                                                        <Input size="sm" value={draftMePayload.to?.address || ''} onChange={(e) => handleToChange('address', e.target.value)} />
                                                    </FormControl>
                                                </GridItem>
                                                <GridItem colSpan={{ base: 12, md: 3 }}>
                                                    <FormControl isRequired>
                                                        <FormLabel fontSize="xs" color="gray.600">Número</FormLabel>
                                                        <Input size="sm" value={draftMePayload.to?.number || ''} onChange={(e) => handleToChange('number', e.target.value)} />
                                                    </FormControl>
                                                </GridItem>
                                                <GridItem colSpan={{ base: 12, md: 12 }}>
                                                    <FormControl>
                                                        <FormLabel fontSize="xs" color="gray.600">Complemento</FormLabel>
                                                        <Input size="sm" value={draftMePayload.to?.complement || ''} onChange={(e) => handleToChange('complement', e.target.value)} />
                                                    </FormControl>
                                                </GridItem>
                                                <GridItem colSpan={{ base: 12, md: 5 }}>
                                                    <FormControl isRequired>
                                                        <FormLabel fontSize="xs" color="gray.600">Bairro</FormLabel>
                                                        <Input size="sm" value={draftMePayload.to?.district || ''} onChange={(e) => handleToChange('district', e.target.value)} />
                                                    </FormControl>
                                                </GridItem>
                                                <GridItem colSpan={{ base: 12, md: 5 }}>
                                                    <FormControl isRequired>
                                                        <FormLabel fontSize="xs" color="gray.600">Cidade</FormLabel>
                                                        <Input size="sm" value={draftMePayload.to?.city || ''} onChange={(e) => handleToChange('city', e.target.value)} />
                                                    </FormControl>
                                                </GridItem>
                                                <GridItem colSpan={{ base: 12, md: 2 }}>
                                                    <FormControl isRequired>
                                                        <FormLabel fontSize="xs" color="gray.600">UF</FormLabel>
                                                        <Input size="sm" maxLength={2} value={draftMePayload.to?.state_abbr || ''} onChange={(e) => handleToChange('state_abbr', e.target.value.toUpperCase())} />
                                                    </FormControl>
                                                </GridItem>
                                            </Grid>
                                        </Box>
                                    </Collapse>
                                </Box>
                            </VStack>
                        ) : null}
                    </DrawerBody>

                    <DrawerFooter bg="white" borderTopWidth="1px">
                        <Button variant="ghost" w="100%" onClick={handleCloseDraftConfig}>Cancelar Edição / Fechar</Button>

                    </DrawerFooter>
                </DrawerContent>
            </Drawer>
        </>
    );
};
